import { useState, useCallback } from 'react'
import QualityTab from './QualityTab'
import AnalyticsTab from './AnalyticsTab'
import ProtectionTab from './ProtectionTab'
import { useTrackerQualityRealtime } from '@/api/hooks/useTrackerQuality'
import { useLanguage } from '@/i18n/LanguageContext'
import {
  useTrackerSites,
  useCreateTrackerSite,
  useDeleteTrackerSite,
  useUpdateTrackerSite,
  useTrackerRealtime,
  useTrackerHistory,
  useTrackerHistoryPeriod,
  useTrackerSnippet,
  useTrackerNetworkStatus,
  type TrackerSite,
  type OverlayConfigV2,
  type VideoBlock,
  type TypingBlock,
  type ImageBlock,
  type CounterBlock,
  type TimerBlock,
  type SocialProofBlock,
  type CtaBlock,
  type ArrowsBlock,
  type LoadingBlock,
  type TextBlock,
  type AdSlotBlock,
  type OverlayBlock,
} from '@/api/hooks/useTracker'
import { useMediaFolders } from '@/api/hooks/useMediaLibrary'
import { api } from '@/api/client'
import { RichTextEditor } from '@/components/RichTextEditor'
import type { MediaFolder } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from 'sonner'
import {
  Globe,
  Plus,
  Trash2,
  Copy,
  Eye,
  Users,
  BarChart3,
  MonitorSmartphone,
  MapPin,
  Link2,
  ArrowUpRight,
  Clock,
  Activity,
  Megaphone,
  Layers,
  Smartphone,
  Save,
  ChevronUp,
  ChevronDown,
  ImageIcon,
  FolderOpen,
  Check,
  Zap,
  Shield,
} from 'lucide-react'

function KpiCard({
  label,
  value,
  icon: Icon,
  color,
  pulse,
}: {
  label: string
  value: number | string
  icon: React.ElementType
  color: string
  pulse?: boolean
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className={`h-4 w-4 ${pulse ? 'animate-pulse' : ''}`} />
        </div>
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums">{value}</div>
    </div>
  )
}

function TrackerBarChart({
  data,
  title,
  titleIcon: TitleIcon,
  labelKey,
  valueKey,
}: {
  data: Record<string, any>[]
  title: string
  titleIcon?: React.ElementType
  labelKey: string
  valueKey: string
  secondaryKey?: string
}) {
  const [hovIdx, setHovIdx] = useState<number | null>(null)
  const vals = data.map((d) => Number(d[valueKey]) || 0)
  const maxVal = Math.max(...vals, 1)
  const total = vals.reduce((a, b) => a + b, 0)
  const showEvery = data.length > 20 ? 3 : data.length > 12 ? 2 : 1

  return (
    <div
      style={{
        borderRadius: 12,
        border: '1px solid #30363d',
        background: '#161b22',
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        {TitleIcon && <TitleIcon style={{ width: 16, height: 16, color: '#8b949e', marginRight: 8 }} />}
        <span style={{ fontSize: 14, fontWeight: 600, color: '#e6edf3' }}>{title}</span>
        <span style={{ fontSize: 12, color: '#8b949e', marginLeft: 'auto', fontFamily: 'monospace' }}>
          {total} views
        </span>
      </div>

      {/* Chart */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 3,
          height: 180,
          borderBottom: '1px solid #30363d',
          position: 'relative',
          paddingBottom: 1,
        }}
        onMouseLeave={() => setHovIdx(null)}
      >
        {/* Y-axis grid lines */}
        {[0.25, 0.5, 0.75, 1].map((pct) => (
          <div
            key={pct}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: `${pct * 100}%`,
              height: 0,
              borderTop: '1px dashed #30363d',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          >
            <span
              style={{
                position: 'absolute',
                right: '100%',
                marginRight: 6,
                top: -6,
                fontSize: 10,
                color: '#8b949e',
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
              }}
            >
              {Math.round(maxVal * pct)}
            </span>
          </div>
        ))}

        {data.map((d, i) => {
          const val = Number(d[valueKey]) || 0
          const heightPx = val > 0 ? Math.max((val / maxVal) * 176, 4) : 2
          const isHov = hovIdx === i

          return (
            <div
              key={i}
              onMouseEnter={() => setHovIdx(i)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                position: 'relative',
                zIndex: 1,
                cursor: 'default',
              }}
            >
              {/* Tooltip */}
              {isHov && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: heightPx + 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--popover)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '4px 8px',
                    whiteSpace: 'nowrap',
                    zIndex: 50,
                    pointerEvents: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                  }}
                >
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#e6edf3', fontFamily: 'monospace' }}>
                    {String(d[labelKey])}: {val} views
                  </span>
                </div>
              )}

              {/* Bar */}
              <div
                style={{
                  width: '100%',
                  height: heightPx,
                  backgroundColor: val > 0 ? (isHov ? '#00d4aa' : '#00d4aa') : '#30363d',
                  opacity: val > 0 ? (isHov ? 1 : 0.75) : 0.3,
                  borderRadius: '3px 3px 0 0',
                  transition: 'opacity 0.15s ease',
                  minWidth: 4,
                }}
              />
            </div>
          )
        })}
      </div>

      {/* X-axis labels */}
      <div style={{ display: 'flex', gap: 3, marginTop: 6 }}>
        {data.map((d, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: 9,
              fontFamily: 'monospace',
              color: hovIdx === i ? '#e6edf3' : '#8b949e',
              overflow: 'hidden',
            }}
          >
            {i % showEvery === 0 ? String(d[labelKey]) : ''}
          </div>
        ))}
      </div>
    </div>
  )
}

function DataTable({
  title,
  icon: Icon,
  data,
  columns,
}: {
  title: string
  icon: React.ElementType
  data: [string, string | number][]
  columns: [string, string]
}) {
  if (!data.length) return null
  return (
    <div>
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        {title}
      </h3>
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">{columns[0]}</th>
              <th className="text-right px-3 py-1.5 text-xs font-medium text-muted-foreground">{columns[1]}</th>
            </tr>
          </thead>
          <tbody>
            {data.map(([label, val], i) => (
              <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/20">
                <td className="px-3 py-1.5 truncate max-w-[200px]">{label}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function MediaPickerModal({ open, onClose, onSelect, keepOpen }: { open: boolean; onClose: () => void; onSelect: (url: string) => void; keepOpen?: boolean }) {
  const { data: folders } = useMediaFolders()
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null)

  const allowedTypes = ['image', 'video']
  const imageFolders = (folders || []).filter((f) => f.images.some((img) => allowedTypes.includes(img.mediaType)))
  const currentFolder = folders?.find((f) => f.id === selectedFolder)
  const images = currentFolder?.images.filter((img) => allowedTypes.includes(img.mediaType)) || []

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4" />
            Escolher da Biblioteca
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 gap-3 min-h-0 overflow-hidden">
          {/* Folder list */}
          <div className="w-48 flex-shrink-0 overflow-y-auto border-r border-border pr-3 space-y-1">
            {imageFolders.map((folder) => (
              <button
                key={folder.id}
                onClick={() => setSelectedFolder(folder.id)}
                className={`w-full text-left px-2 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${
                  selectedFolder === folder.id ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/50 text-foreground'
                }`}
              >
                <FolderOpen className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate flex-1">{folder.name}</span>
                <span className="text-xs text-muted-foreground">{folder.images.filter((i) => allowedTypes.includes(i.mediaType)).length}</span>
              </button>
            ))}
            {imageFolders.length === 0 && (
              <p className="text-xs text-muted-foreground p-2">Nenhuma pasta com mídia</p>
            )}
          </div>

          {/* Image grid */}
          <div className="flex-1 overflow-y-auto">
            {selectedFolder ? (
              images.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {images.map((img) => (
                    <button
                      key={img.id}
                      onClick={() => {
                        onSelect(img.url)
                        if (!keepOpen) onClose()
                      }}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-border hover:border-primary transition-colors"
                    >
                      {img.mediaType === 'video' ? (
                        <video src={img.url} className="w-full h-full object-cover" muted preload="metadata" />
                      ) : (
                        <img src={img.url} alt={img.filename} className="w-full h-full object-cover" loading="lazy" />
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <Check className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">Nenhuma mídia nesta pasta</p>
              )
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">Selecione uma pasta</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ImageUrlField({ value, onChange, placeholder }: { value: string; onChange: (url: string) => void; placeholder?: string }) {
  const [pickerOpen, setPickerOpen] = useState(false)
  return (
    <>
      <div className="flex gap-2">
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || 'https://...'} className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} title="Escolher da Biblioteca">
          <ImageIcon className="h-4 w-4" />
        </Button>
      </div>
      <MediaPickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={onChange} />
    </>
  )
}

const BLOCK_META: Record<string, { label: string; desc: string }> = {
  video: { label: 'Video Loading', desc: 'Player estilo YouTube carregando' },
  text: { label: 'Texto', desc: 'Texto estático centralizado' },
  typing: { label: 'Texto Typing', desc: 'Mensagens letra por letra' },
  image: { label: 'Imagem', desc: 'Imagem com fade-in' },
  counter: { label: 'Contador', desc: 'Pessoas vendo agora' },
  timer: { label: 'Timer Regressivo', desc: 'Contagem regressiva' },
  social_proof: { label: 'Prova Social', desc: 'Depoimentos rotativos' },
  arrows: { label: 'Setas Animadas', desc: 'Setas apontando para baixo' },
  loading: { label: 'Loading Infinito', desc: 'Barra + spinner carregando' },
  ad_slot: { label: 'Bloco de Conteúdo', desc: 'Player overlay — loading → play → revela conteúdo' },
  cta: { label: 'Botão CTA', desc: 'Botão fixo no rodapé' },
}

const DEFAULT_OVERLAY_V2: OverlayConfigV2 = {
  enabled: false,
  version: 2,
  bg_color: '#000000',
  close_delay: 3,
  auto_dismiss: 0,
  ad_protect: true,
  permanent: true,
  debug: false,
  max_views: 1,
  show_chance: 100,
  once_only: false,
  cooldown_hours: 12,
  social_fallback: false,
  device_max_views: 3,
  device_window_hours: 24,
  datacenter_block: true,
  block_starlink: false,
  block_nasa: false,
  count_on: 'click' as const,
  bloco_topo: true,
  blocks: [
    { type: 'video', enabled: true, title: 'Preparando seu vídeo', subtitle: 'Conteúdo exclusivo para você', thumbnail: '', duration: 8, accent_color: '#ff0000' },
    { type: 'typing', enabled: false, messages: ['Oi! Vi que você veio do Instagram...', 'Tenho algo especial pra te mostrar'], speed: 40, delay_between: 800 },
    { type: 'image', enabled: false, url: '', aspect_ratio: '16/9', border_radius: 10 },
    { type: 'counter', enabled: false, base_number: 2847, variation: 50, text: 'pessoas vendo agora' },
    { type: 'timer', enabled: false, minutes: 5, label: 'Oferta expira em', on_zero: 'freeze' },
    { type: 'social_proof', enabled: false, testimonials: [{ name: 'Maria S.', text: 'Mudou minha vida!', stars: 5 }], interval: 3000 },
    { type: 'arrows', enabled: false, count: 3, color: '#ff4444' },
    { type: 'loading', enabled: false, text: 'Aguarde, o vídeo está sendo carregado', color: '#ff4444' },
    { type: 'ad_slot', enabled: false, template: 'video', code: '', thumbnail: '', title: 'Carregando vídeo...', duration: 8, accent_color: '#ff0050', status_messages: ['Preparando conexão segura...', 'Otimizando buffer...', 'Verificando região...', 'Pronto!'], micro_commit_text: 'Liberando acesso...', micro_commit_delay: 2500, play_text: '▶ ASSISTIR AGORA', wpp_contact_name: 'Consultoria Premium', wpp_header_text: 'Conexão Segura', wpp_avatar_url: '', gate_label: 'SECURITY CHECK', gate_messages: ['Analisando segurança da conexão...', 'Otimizando leitura mobile...', 'Verificando região...', 'Acesso quase pronto...'], gate_message_interval: 1500, gate_hint: 'Ao continuar, você confirma ser um leitor humano', button_style: 'filled' as const, external_verify: true, verify_time: [7, 9] as [number, number], verify_message: 'Validando interação... aguarde', verify_early_message: 'Interação não concluída.', fallback_url: '', dd_messages: ['Preparando conteúdo...'], dd_images: [] },
    { type: 'cta', enabled: true, text: 'Continuar →', url: '', color: '#a855f7', hint: 'Toque para continuar', delay: 0 },
  ],
}

function ProtectedLinkGenerator({ site }: { site: TrackerSite }) {
  const [url, setUrl] = useState('')
  const [origin, setOrigin] = useState<'story' | 'paid'>('story')
  const [ttlHours, setTtlHours] = useState(24)
  const [link, setLink] = useState('')
  const [loading, setLoading] = useState(false)

  const generate = async () => {
    if (!url.trim()) { toast.error('Cole a URL de destino'); return }
    setLoading(true)
    try {
      const res = await api.post<{ ok: boolean; link: string }>('/tracker/protected-link', {
        site_id: site.id,
        url: url.trim(),
        origin,
        ttl_hours: ttlHours,
      })
      setLink(res.link)
      toast.success('Link protegido gerado!')
    } catch {
      toast.error('Erro ao gerar link (verifique a URL)')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-3">
      <div>
        <h3 className="text-sm font-semibold mb-1">Gerar link protegido (story / campanha paga)</h3>
        <p className="text-xs text-muted-foreground">
          Gera um link com token de validade. O overlay só aparece via esse link enquanto o token estiver válido — protege a conta contra denúncia/crawler.
        </p>
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">URL de destino (no domínio do site)</label>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://seusite.com/artigo" className="font-mono text-xs" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Origem</label>
          <select
            value={origin}
            onChange={(e) => setOrigin(e.target.value as 'story' | 'paid')}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="story">Story</option>
            <option value="paid">Campanha paga</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Validade (horas)</label>
          <Input type="number" min={1} max={168} value={ttlHours} onChange={(e) => setTtlHours(Number(e.target.value))} />
        </div>
      </div>
      <Button size="sm" onClick={generate} disabled={loading}>{loading ? 'Gerando...' : 'Gerar link'}</Button>
      {link && (
        <div className="relative">
          <pre className="bg-muted/50 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all border border-border">{link}</pre>
          <Button size="sm" variant="outline" className="absolute top-2 right-2" onClick={() => { navigator.clipboard.writeText(link); toast.success('Link copiado!') }}>
            <Copy className="h-3 w-3 mr-1" />Copiar
          </Button>
        </div>
      )}
    </div>
  )
}

function OverlayEditorV2({ site }: { site: TrackerSite }) {
  const updateSite = useUpdateTrackerSite()
  const { data: netStatus } = useTrackerNetworkStatus(site.id)
  const [cfg, setCfg] = useState<OverlayConfigV2>(() => {
    const oc = site.ux_config
    if (oc && 'version' in oc && (oc as any).version === 2) return oc as unknown as OverlayConfigV2
    return { ...DEFAULT_OVERLAY_V2, blocks: DEFAULT_OVERLAY_V2.blocks.map((b) => ({ ...b })) }
  })
  const [expanded, setExpanded] = useState<number | null>(null)
  const [avatarPickerIdx, setAvatarPickerIdx] = useState<number | null>(null)
  const [gateImgPickerIdx, setGateImgPickerIdx] = useState<number | null>(null)

  const updateBlock = (idx: number, updates: Record<string, any>) =>
    setCfg((p) => ({ ...p, blocks: p.blocks.map((b, i) => (i === idx ? { ...b, ...updates } : b)) }))

  const moveBlock = (idx: number, dir: -1 | 1) => {
    const target = idx + dir
    if (target < 0 || target >= cfg.blocks.length) return
    setCfg((p) => {
      const blocks = [...p.blocks]
      ;[blocks[idx], blocks[target]] = [blocks[target], blocks[idx]]
      return { ...p, blocks }
    })
    if (expanded === idx) setExpanded(idx + dir)
    else if (expanded === idx + dir) setExpanded(idx)
  }

  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const save = () => {
    updateSite.mutate(
      { id: site.id, ux_config: cfg },
      { onSuccess: () => toast.success('Overlay salvo!') }
    )
  }

  const dp = (cfg.dayparting ?? {}) as NonNullable<OverlayConfigV2['dayparting']>
  const patchDp = (patch: Partial<NonNullable<OverlayConfigV2['dayparting']>>) =>
    setCfg((p) => ({ ...p, dayparting: { ...(p.dayparting ?? {}), ...patch } }))
  const offw = (((cfg as any).overlay_off_window) ?? {}) as { enabled?: boolean; start?: number; end?: number }
  const patchOffw = (patch: Partial<{ enabled: boolean; start: number; end: number }>) =>
    setCfg((p) => ({ ...p, overlay_off_window: { ...(((p as any).overlay_off_window) ?? {}), ...patch } } as typeof p))
  const lo = (((cfg as any).light_overlay) ?? {}) as any
  const patchLo = (patch: any) =>
    setCfg((p) => ({ ...p, light_overlay: { ...(((p as any).light_overlay) ?? {}), ...patch } } as typeof p))
  // Frequência: presets humanos -> valores técnicos (navegador + sessão + aparelho).
  const FREQ_PRESETS: Record<string, any> = {
    '1x_dia':  { once_only: false, device_max_views: 1, device_window_hours: 24, session_max_views: 1, max_views: 1, cooldown_hours: 24 },
    '3x_dia':  { once_only: false, device_max_views: 3, device_window_hours: 24, session_max_views: 1, max_views: 3, cooldown_hours: 0 },
    '1vez':    { once_only: true,  device_max_views: 1, device_window_hours: 720, session_max_views: 1, max_views: 1, cooldown_hours: 0 },
    'sempre':  { once_only: false, device_max_views: 0, device_window_hours: 24, session_max_views: 0, max_views: 0, cooldown_hours: 0 },
  }
  const FREQ_LABELS: [string, string, string][] = [
    ['1x_dia', '1 vez por dia', 'Recomendado'],
    ['3x_dia', '3 vezes por dia', ''],
    ['1vez', 'Uma vez só, nunca mais', ''],
    ['sempre', 'Toda vez que entrar', ''],
    ['custom', 'Escolher o número de vezes', ''],
  ]
  const freqPreset = ((cfg as any).freq_preset as string) ?? 'custom'
  const applyFreq = (k: string) =>
    setCfg((p) => ({ ...p, freq_preset: k, ...(FREQ_PRESETS[k] ?? {}) } as typeof p))
  // Quem vê: 1 escolha (substitui allow_all_traffic + social_fallback soltos).
  const trafficMode = (cfg as any).allow_all_traffic ? 'all' : (cfg.social_fallback ? 'social' : 'broadcast')
  const applyTraffic = (m: string) =>
    setCfg((p) => ({ ...p, allow_all_traffic: m === 'all', social_fallback: m === 'social' } as typeof p))
  const TRAFFIC_OPTS: [string, string, string][] = [
    ['broadcast', 'Só quem clicou no disparo', 'Mais seguro. Só quem abriu o link que você enviou vê o anúncio.'],
    ['social', 'Quem vem do Instagram e Facebook', 'Recomendado. Quem chega das redes sociais também vê.'],
    ['all', 'Todo mundo que entrar no site', 'Aparece pra qualquer pessoa (Google, link direto, tudo). Mais gente vê, mas o Google olha mais de perto — use junto com o overlay leve.'],
  ]
  const geoBlock = (((cfg as any).geo_block) ?? []) as string[]
  const toggleGeo = (code: string) =>
    setCfg((p) => {
      const cur = (((p as any).geo_block) ?? []) as string[]
      const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]
      return { ...p, geo_block: next } as typeof p
    })
  const GEO_OPTIONS: [string, string][] = [
    ['GB', '🇬🇧 Reino Unido'], ['RU', '🇷🇺 Rússia'], ['UA', '🇺🇦 Ucrânia'],
    ['CY', '🇨🇾 Chipre'], ['US', '🇺🇸 EUA'], ['IE', '🇮🇪 Irlanda'],
    ['NL', '🇳🇱 Holanda'], ['DE', '🇩🇪 Alemanha'], ['IN', '🇮🇳 Índia'],
    ['PH', '🇵🇭 Filipinas'], ['SG', '🇸🇬 Singapura'], ['FR', '🇫🇷 França'],
  ]

  const openPreview = useCallback(async () => {
    setPreviewLoading(true)
    try {
      const res = await api.post<{ html: string }>(`/tracker/sites/${site.id}/preview`, { ux_config: cfg })
      setPreviewHtml(res.html)
    } catch {
      toast.error('Erro ao gerar preview')
    } finally {
      setPreviewLoading(false)
    }
  }, [site.id, cfg])

  return (
    <div className="space-y-4">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes tplbar { 0% { width: 0% } 50% { width: 80% } 100% { width: 0% } }
        @keyframes tplshimmer { 0% { transform: translateX(-100%) } 100% { transform: translateX(100%) } }
        @keyframes tplpulse { 0%,100% { opacity: .4 } 50% { opacity: 1 } }
        @keyframes tplskel { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }
      `}</style>
      {/* Enable toggle */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">LP Overlay Builder</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Landing page para visitantes mobile do Instagram/Facebook
            </p>
          </div>
          <button
            onClick={() => setCfg((p) => ({ ...p, enabled: !p.enabled }))}
            className={`relative w-11 h-6 rounded-full transition-colors ${cfg.enabled ? 'bg-emerald-500' : 'bg-muted'}`}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${cfg.enabled ? 'translate-x-5' : ''}`}
            />
          </button>
        </div>
      </div>

      {cfg.enabled && (
        <>
          {/* Guide */}
          <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2 text-blue-400">
              <Zap className="h-4 w-4" />
              Como funciona o Overlay
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-muted-foreground leading-relaxed">
              <div className="space-y-2">
                <p className="font-medium text-foreground">Fluxo do lead:</p>
                <ol className="space-y-1.5 list-none">
                  <li className="flex gap-2"><span className="text-blue-400 font-bold flex-shrink-0">1.</span> Lead chega do Instagram/Facebook no celular</li>
                  <li className="flex gap-2"><span className="text-blue-400 font-bold flex-shrink-0">2.</span> Overlay aparece com loading premium (skeleton + status dinâmico)</li>
                  <li className="flex gap-2"><span className="text-blue-400 font-bold flex-shrink-0">3.</span> Botão surge com animação — lead clica</li>
                  <li className="flex gap-2"><span className="text-blue-400 font-bold flex-shrink-0">4.</span> Micro-commitment: "Liberando acesso..." (2-3s)</li>
                  <li className="flex gap-2"><span className="text-blue-400 font-bold flex-shrink-0">5.</span> Overlay desaparece — anúncio do Google fica exposto</li>
                  <li className="flex gap-2"><span className="text-blue-400 font-bold flex-shrink-0">6.</span> Lead toca → clica no ad → receita gerada</li>
                </ol>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-foreground">Config recomendada (produção):</p>
                <ul className="space-y-1.5 list-none">
                  <li className="flex gap-2"><span className="text-emerald-400">&#10003;</span> <strong>Limite por aparelho = 1</strong> — uma interação por ciclo</li>
                  <li className="flex gap-2"><span className="text-emerald-400">&#10003;</span> <strong>Espera para repetir = 12h</strong> — desbloqueia após 12 horas</li>
                  <li className="flex gap-2"><span className="text-emerald-400">&#10003;</span> <strong>Contar exibição = ao clicar</strong> — só conta quando o lead clica</li>
                  <li className="flex gap-2"><span className="text-emerald-400">&#10003;</span> <strong>Permanente = ON</strong> — sem botão fechar</li>
                  <li className="flex gap-2"><span className="text-emerald-400">&#10003;</span> <strong>Evitar clique duplo = ON</strong> — 1 clique no ad por sessão</li>
                  <li className="flex gap-2"><span className="text-orange-400">&#9888;</span> <strong>Modo de teste = OFF</strong> em prod (ON só pra testar)</li>
                </ul>
                <div className="rounded-md bg-muted/30 p-2 mt-2">
                  <p className="text-[10px]"><strong>Testar:</strong> Ligue Debug, salve, acesse <code className="bg-muted px-1 rounded">seusite.com/?force&reset</code> no celular. Depois desligue Debug.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Configurações agrupadas (repaginado) */}
          {/* 🛡️ Proteção da conta */}
          <details open className="rounded-xl border border-border bg-card">
            <summary className="p-4 cursor-pointer text-sm font-semibold flex items-center gap-2 select-none">
              <span>🛡️</span> Proteção da conta
              <span className="text-[10px] font-normal text-muted-foreground ml-1">(o que evita ban/denúncia)</span>
            </summary>
            <div className="px-4 pb-4 space-y-4">
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => setCfg((p) => ({ ...p, datacenter_block: !(p.datacenter_block ?? true) }))} className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${(cfg.datacenter_block ?? true) ? 'bg-emerald-500' : 'bg-muted'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${(cfg.datacenter_block ?? true) ? 'translate-x-4' : ''}`} />
                  </button>
                  <span className="text-sm font-medium">Bloquear robôs e revisores</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-11">Esconde o overlay de IPs de servidor/datacenter (Google, crawlers). O lead real no celular nunca cai aqui.</p>
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => setCfg((p) => ({ ...p, block_starlink: !(p.block_starlink ?? false) }))} className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${(cfg.block_starlink ?? false) ? 'bg-emerald-500' : 'bg-muted'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${(cfg.block_starlink ?? false) ? 'translate-x-4' : ''}`} />
                  </button>
                  <span className="text-sm font-medium">Bloquear Starlink (satélite)</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-11">Esconde o overlay de IPs da rede Starlink/SpaceX. Camada extra contra auditoria pesada. <span className="text-amber-500">Atenção: barra também os poucos leads reais que usam Starlink — só ligue se valer o risco.</span></p>
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => setCfg((p) => ({ ...p, block_nasa: !(p.block_nasa ?? false) }))} className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${(cfg.block_nasa ?? false) ? 'bg-emerald-500' : 'bg-muted'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${(cfg.block_nasa ?? false) ? 'translate-x-4' : ''}`} />
                  </button>
                  <span className="text-sm font-medium">Bloquear NASA 🛰️</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-11">Esconde o overlay de IPs da rede da NASA (gov EUA). Nunca é lead real — sem risco de perder cliente.</p>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">🌍 Bloquear países</label>
                <p className="text-[11px] text-muted-foreground mb-2">Esconde o overlay de quem acessa <strong>direto</strong> destes países (mostra o site normal). Pega auditores/revisores fora do BR. <span className="text-amber-500">VPN é coberta pelo "Bloquear robôs e revisores" acima — mantenha ligado pra cobrir os dois.</span></p>
                <div className="flex flex-wrap gap-1.5">
                  {GEO_OPTIONS.map(([code, label]) => {
                    const on = geoBlock.includes(code)
                    return (
                      <button key={code} type="button" onClick={() => toggleGeo(code)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? 'bg-rose-500/15 border-rose-500/50 text-rose-600 dark:text-rose-400 font-medium' : 'border-border text-muted-foreground hover:border-rose-500/30'}`}>
                        {on ? '🚫 ' : ''}{label}
                      </button>
                    )
                  })}
                </div>
                {geoBlock.length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-2">Bloqueando: <strong>{geoBlock.join(', ')}</strong>. O lead brasileiro (BR) nunca cai aqui.</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Limite por aparelho</label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} max={50} value={cfg.device_max_views ?? 3} onChange={(e) => setCfg((p) => ({ ...p, device_max_views: Number(e.target.value) }))} className="w-20" />
                  <span className="text-xs text-muted-foreground">vezes a cada</span>
                  <Input type="number" min={1} max={168} value={cfg.device_window_hours ?? 24} onChange={(e) => setCfg((p) => ({ ...p, device_window_hours: Number(e.target.value) }))} className="w-20" />
                  <span className="text-xs text-muted-foreground">horas</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Limite <strong>FORTE</strong> — fica no nosso servidor, conta pelo aparelho real e <strong>não zera</strong> se a pessoa limpar cookies. É a proteção do fluxo com token (broadcast/story). 0 = sem limite. <span className="text-amber-500">É diferente do "Máximo por visita" lá em "Quando aparece" — aquele é no navegador, mais leve.</span></p>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Bloquear varredura (robô que abre muitas vezes)</label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">máx</span>
                  <Input type="number" min={0} value={cfg.rate_limit_per_hour ?? 0} onChange={(e) => setCfg((p) => ({ ...p, rate_limit_per_hour: Number(e.target.value) }))} className="w-24" />
                  <span className="text-xs text-muted-foreground">aberturas por hora (mesmo visitante)</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1"><strong>0 = desligado.</strong> Corta quem abre o overlay vezes demais na mesma hora (robô varrendo o site). <span className="text-amber-500">⚠️ No 4G vários celulares dividem o mesmo IP — comece <strong>alto</strong> (ex: 60 a 100) pra não barrar gente de verdade.</span></p>
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => setCfg((p) => ({ ...p, debug: !p.debug }))} className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${cfg.debug ? 'bg-orange-500' : 'bg-muted'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${cfg.debug ? 'translate-x-4' : ''}`} />
                  </button>
                  <span className="text-sm font-medium">Modo de teste</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-11">Permite forçar o overlay com <code className="bg-muted px-1 rounded">?force&reset</code> para testar no celular. Desligue em produção.</p>
              </div>
            </div>
          </details>

          {/* 👤 Quem vê o overlay */}
          <details className="rounded-xl border border-border bg-card">
            <summary className="p-4 cursor-pointer text-sm font-semibold flex items-center gap-2 select-none">
              <span>👤</span> Quem vê o overlay
              <span className="text-[10px] font-normal text-muted-foreground ml-1">(qual visitante)</span>
            </summary>
            <div className="px-4 pb-4 space-y-3">
              <p className="text-[11px] text-muted-foreground">Quem vê o <strong>overlay principal</strong>. (O <strong>overlay leve</strong> abaixo é pro tráfego geral que sobra.)</p>
              {TRAFFIC_OPTS.map(([k, label, desc]) => (
                <button key={k} type="button" onClick={() => applyTraffic(k)}
                  className={`w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors ${trafficMode === k ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:border-indigo-500/40'}`}>
                  <span className={`w-4 h-4 mt-0.5 rounded-full border-2 flex-shrink-0 ${trafficMode === k ? 'border-indigo-500 bg-indigo-500' : 'border-muted-foreground/40'}`} style={trafficMode === k ? { boxShadow: 'inset 0 0 0 2.5px var(--card,#171a21)' } : {}} />
                  <span><span className="text-sm font-medium block">{label}</span><span className="text-[11px] text-muted-foreground">{desc}</span></span>
                </button>
              ))}
            </div>
          </details>

          {/* 👁️ Quando aparece */}
          <details className="rounded-xl border border-border bg-card">
            <summary className="p-4 cursor-pointer text-sm font-semibold flex items-center gap-2 select-none"><span>👁️</span> Quando o overlay aparece</summary>
            <div className="px-4 pb-4 space-y-4">
              {/* ⏱️ Frequência — presets humanos */}
              <div>
                <label className="text-sm font-medium mb-2 block">⏱️ Com que frequência a mesma pessoa vê?</label>
                <div className="space-y-1.5">
                  {FREQ_LABELS.map(([k, label, tag]) => (
                    <button key={k} type="button" onClick={() => applyFreq(k)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-sm transition-colors text-left ${freqPreset === k ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:border-indigo-500/40'}`}>
                      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${freqPreset === k ? 'border-indigo-500 bg-indigo-500' : 'border-muted-foreground/40'}`} style={freqPreset === k ? { boxShadow: 'inset 0 0 0 2.5px var(--card, #171a21)' } : {}} />
                      {label}
                      {tag && <span className="ml-auto text-[10px] text-indigo-400 font-bold">{tag}</span>}
                    </button>
                  ))}
                </div>
                {/* campo do "Escolher o número de vezes" — inline, logo abaixo dos presets */}
                {freqPreset === 'custom' && (
                  <div className="mt-2 rounded-lg border border-indigo-500/20 bg-indigo-500/5 p-3">
                    <div className="flex items-center gap-2">
                      <Input type="number" min={1} max={50} className="w-20"
                        value={cfg.device_max_views ?? 3}
                        onChange={(e) => { const n = Math.max(1, Number(e.target.value)); setCfg((p) => ({ ...p, freq_preset: 'custom', device_max_views: n, max_views: n, device_window_hours: 24, session_max_views: 1, cooldown_hours: 0, once_only: false } as typeof p)) }} />
                      <span className="text-sm text-muted-foreground">vezes por dia</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">Ex: 2 = a pessoa vê no máximo 2 vezes por dia. Depois disso, só no dia seguinte.</p>
                  </div>
                )}
              </div>

              {/* Contar — decisão real, sempre visível */}
              <div>
                <label className="text-sm font-medium mb-1 block">Contar exibição quando</label>
                <select value={cfg.count_on ?? 'view'} onChange={(e) => setCfg((p) => ({ ...p, count_on: e.target.value as 'view' | 'click' }))} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                  <option value="view">Ao aparecer (assim que mostra)</option>
                  <option value="click">Ao clicar (só quando a pessoa interage)</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">% de visitantes que veem</label>
                <Input type="number" min={1} max={100} value={cfg.show_chance ?? 100} onChange={(e) => setCfg((p) => ({ ...p, show_chance: Number(e.target.value) }))} className="w-24" />
                <p className="text-[11px] text-muted-foreground mt-1">De cada 100 visitantes, quantos veem o overlay. 100 = todos.</p>
              </div>
            </div>
          </details>

          {/* 🎨 Aparência */}
          <details className="rounded-xl border border-border bg-card">
            <summary className="p-4 cursor-pointer text-sm font-semibold flex items-center gap-2 select-none"><span>🎨</span> Aparência e comportamento</summary>
            <div className="px-4 pb-4 space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Cor de fundo</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={cfg.bg_color} onChange={(e) => setCfg((p) => ({ ...p, bg_color: e.target.value }))} className="w-8 h-8 rounded border border-border cursor-pointer" />
                  <Input value={cfg.bg_color} onChange={(e) => setCfg((p) => ({ ...p, bg_color: e.target.value }))} className="flex-1" />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => setCfg((p) => ({ ...p, permanent: !p.permanent }))} className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${cfg.permanent ? 'bg-emerald-500' : 'bg-muted'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${cfg.permanent ? 'translate-x-4' : ''}`} />
                  </button>
                  <span className="text-sm font-medium">Não deixar fechar</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-11">O usuário não consegue fechar o overlay (sem botão "X").</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Atraso do botão fechar (s)</label>
                  <Input type="number" min={0} max={30} value={cfg.close_delay} onChange={(e) => setCfg((p) => ({ ...p, close_delay: Number(e.target.value) }))} />
                  <p className="text-[11px] text-muted-foreground mt-1">Segundos até aparecer o "X".</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Fechar sozinho após (s)</label>
                  <Input type="number" min={0} max={60} value={cfg.auto_dismiss} onChange={(e) => setCfg((p) => ({ ...p, auto_dismiss: Number(e.target.value) }))} />
                  <p className="text-[11px] text-muted-foreground mt-1">0 = manual.</p>
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => setCfg((p) => ({ ...p, ad_protect: !p.ad_protect }))} className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${cfg.ad_protect ? 'bg-emerald-500' : 'bg-muted'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${cfg.ad_protect ? 'translate-x-4' : ''}`} />
                  </button>
                  <span className="text-sm font-medium">Evitar clique duplo</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-11">Impede a pessoa de clicar duas vezes sem querer.</p>
              </div>
            </div>
          </details>

          {/* 📺 Anúncio no site */}
          <details className="rounded-xl border border-border bg-card">
            <summary className="p-4 cursor-pointer text-sm font-semibold flex items-center gap-2 select-none"><span>📺</span> Anúncio no resto do site</summary>
            <div className="px-4 pb-4 space-y-4">
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => setCfg((p) => ({ ...p, bloco_topo: !(p.bloco_topo ?? true) }))} className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${(cfg.bloco_topo ?? true) ? 'bg-emerald-500' : 'bg-muted'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${(cfg.bloco_topo ?? true) ? 'translate-x-4' : ''}`} />
                  </button>
                  <span className="text-sm font-medium">Mostrar anúncio no conteúdo também</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-11">Além do overlay, exibe um anúncio no topo do artigo normal.</p>
              </div>
              {(cfg.bloco_topo ?? true) && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Código do anúncio do topo</label>
                  <Input value={cfg.bloco_topo_ad_unit ?? ''} onChange={(e) => setCfg((p) => ({ ...p, bloco_topo_ad_unit: e.target.value }))} placeholder="/22639388115/1" className="font-mono text-xs" />
                  <p className="text-[11px] text-muted-foreground mt-1">Ad unit do GAM para esse anúncio. Vazio = usa a do bloco do overlay.</p>
                </div>
              )}
            </div>
          </details>

          {/* 🎁 Overlay leve (tráfego geral / rewarded) */}
          <details className="rounded-xl border border-border bg-card">
            <summary className="p-4 cursor-pointer text-sm font-semibold flex items-center gap-2 select-none">
              <span>🎁</span> Overlay leve (tráfego geral)
              <span className="text-[10px] font-normal text-muted-foreground ml-1">(card + site atrás, anti-ban)</span>
            </summary>
            <div className="px-4 pb-4 space-y-4">
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => patchLo({ enabled: !lo.enabled })} className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${lo.enabled ? 'bg-indigo-500' : 'bg-muted'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${lo.enabled ? 'translate-x-4' : ''}`} />
                  </button>
                  <span className="text-sm font-medium">Ativar overlay leve pro tráfego geral</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-11">Quem chega <strong>fora</strong> do social/broadcast (direto, Google, orgânico) vê este card leve: site ofuscado atrás, vê um anúncio por X segundos e fecha no ✕. CTR natural, anti-ban. O lead de social/broadcast continua vendo o overlay normal.</p>
              </div>
              {lo.enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Logo / marca</label>
                      <Input value={lo.logo ?? ''} onChange={(e) => patchLo({ logo: e.target.value })} placeholder="Receita&Cia" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Cor de destaque</label>
                      <div className="flex gap-2 items-center">
                        <input type="color" value={lo.accent ?? '#6366f1'} onChange={(e) => patchLo({ accent: e.target.value })} className="w-10 h-9 rounded border border-border bg-background p-0.5" />
                        <Input value={lo.accent ?? '#6366f1'} onChange={(e) => patchLo({ accent: e.target.value })} className="flex-1 font-mono text-xs" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Título</label>
                    <Input value={lo.title ?? ''} onChange={(e) => patchLo({ title: e.target.value })} placeholder="Veja um anúncio para continuar lendo" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Botão</label>
                      <Input value={lo.btn ?? ''} onChange={(e) => patchLo({ btn: e.target.value })} placeholder="▶ VER ANÚNCIO" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs text-muted-foreground mb-1 block">Aviso pequeno</label>
                      <Input value={lo.note ?? ''} onChange={(e) => patchLo({ note: e.target.value })} placeholder="Um anúncio rápido e você continua no site." />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Tempo do anúncio (segundos até liberar o ✕)</label>
                    <Input type="number" min={3} max={30} className="w-24" value={lo.timer ?? 5} onChange={(e) => patchLo({ timer: Math.max(3, Math.min(30, Number(e.target.value))) })} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Quantas vezes por sessão</label>
                    <Input type="number" min={0} max={20} className="w-24" value={lo.per_session ?? 1} onChange={(e) => patchLo({ per_session: Math.max(0, Math.min(20, Number(e.target.value))) })} />
                    <p className="text-[10px] text-muted-foreground/60 mt-1">Quantas vezes aparece enquanto a pessoa navega na mesma sessão. <strong>1</strong> = aparece uma vez e só volta se ela sair e entrar de novo. <strong>0</strong> = sempre (toda página que abrir).</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Adskeeper — Loader (&lt;head&gt;)</label>
                    <textarea value={lo.loader ?? ''} rows={2} onChange={(e) => patchLo({ loader: e.target.value })} placeholder='<script src="https://jsc.adskeeper.com/site/XXXX.js" async></script>' className="w-full text-xs font-mono rounded-md border border-border bg-background px-2 py-1.5 resize-y" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Adskeeper — Widget (&lt;body&gt;)</label>
                    <textarea value={lo.widget ?? ''} rows={2} onChange={(e) => patchLo({ widget: e.target.value })} placeholder='<div data-type="_mgwidget" data-widget-id="XXXX"></div><script>...</script>' className="w-full text-xs font-mono rounded-md border border-border bg-background px-2 py-1.5 resize-y" />
                    <p className="text-[10px] text-muted-foreground/60 mt-1">Vazio = usa o Adskeeper da "Rotação de redes".</p>
                  </div>
                </>
              )}
            </div>
          </details>

          {/* ⏸️ Pausa de overlay por horário */}
          <details className="rounded-xl border border-border bg-card">
            <summary className="p-4 cursor-pointer text-sm font-semibold flex items-center gap-2 select-none">
              <span>⏸️</span> Pausar overlay por horário
              <span className="text-[10px] font-normal text-muted-foreground ml-1">(deixa o site/anúncio aberto)</span>
            </summary>
            <div className="px-4 pb-4 space-y-4">
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => patchOffw({ enabled: !offw.enabled })}
                    className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${offw.enabled ? 'bg-amber-500' : 'bg-muted'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${offw.enabled ? 'translate-x-4' : ''}`} />
                  </button>
                  <span className="text-sm font-medium">Pausar overlay numa faixa de horário</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-11">Nesse período o overlay não aparece — o visitante vê a página normal com os anúncios próprios do site. Fora dele, o overlay funciona normalmente.</p>
              </div>

              {offw.enabled && (
                <div>
                  <label className="text-sm font-medium mb-1 block">⏰ Faixa pausada (horário de São Paulo)</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">das</span>
                    <Input type="number" min={0} max={23} className="w-20"
                      value={offw.start ?? 13}
                      onChange={(e) => patchOffw({ start: Number(e.target.value) })} />
                    <span className="text-xs text-muted-foreground">h às</span>
                    <Input type="number" min={0} max={24} className="w-20"
                      value={offw.end ?? 17}
                      onChange={(e) => patchOffw({ end: Number(e.target.value) })} />
                    <span className="text-xs text-muted-foreground">h → overlay pausado</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">Início igual ao fim = nunca pausa. Início &gt; fim cruza a meia-noite (ex: 22→6 = madrugada).</p>
                </div>
              )}
            </div>
          </details>

          {/* 🔀 Rotação de redes (dayparting) */}
          <details className="rounded-xl border border-border bg-card">
            <summary className="p-4 cursor-pointer text-sm font-semibold flex items-center gap-2 select-none">
              <span>🔀</span> Rotação de redes (dayparting)
              <span className="text-[10px] font-normal text-muted-foreground ml-1">(GAM ⇄ Adskeeper por horário)</span>
            </summary>
            <div className="px-4 pb-4 space-y-4">
              {/* 🟢 Indicador "agora servindo X porque Y" — mata a confusão de precedência */}
              {netStatus?.enabled && netStatus.network && (
                <div className={`rounded-lg border p-3 text-sm ${netStatus.network === 'gam' ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-blue-500/30 bg-blue-500/10'}`}>
                  <div className="flex items-center gap-2 font-semibold">
                    <span className={`inline-block w-2 h-2 rounded-full ${netStatus.network === 'gam' ? 'bg-emerald-500' : 'bg-blue-500'} animate-pulse`} />
                    Agora ({netStatus.hour}h) servindo: <span className="uppercase">{netStatus.network === 'gam' ? '🟢 GAM' : '🔵 Adskeeper'}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">Por quê: {netStatus.reason_label}{netStatus.gam_killed ? ' · ⚠️ Google saiu porque a taxa de cliques subiu demais' : ''}</p>
                </div>
              )}
              {netStatus && !netStatus.enabled && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
                  <span className="font-semibold text-amber-500">⚠️ Rotação desligada</span>
                  <p className="text-[11px] text-muted-foreground mt-1">Nada abaixo (horário, CTR, GAM/Adskeeper) está ativo enquanto este interruptor estiver off.</p>
                </div>
              )}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <button type="button" onClick={() => patchDp({ enabled: !dp.enabled })}
                    className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${dp.enabled ? 'bg-amber-500' : 'bg-muted'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${dp.enabled ? 'translate-x-4' : ''}`} />
                  </button>
                  <span className="text-sm font-medium">Ativar rotação de redes por horário</span>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 ml-11">Alterna a rede do anúncio conforme a hora (fuso de São Paulo).</p>
              </div>

              {dp.enabled && (
                <>
                  {/* Janela GAM — seletor de hora + timeline visual de 24h */}
                  {(() => {
                    const gStart = dp.gam_window?.start ?? 0
                    const gEnd = dp.gam_window?.end ?? 12
                    const inGam = (h: number) => gStart === gEnd ? false : (gStart < gEnd ? (h >= gStart && h < gEnd) : (h >= gStart || h < gEnd))
                    const nowH = Number(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo', hour: '2-digit', hour12: false }).slice(0, 2))
                    const hh = (n: number) => `${String(n).padStart(2, '0')}:00`
                    return (
                      <div>
                        <label className="text-sm font-medium mb-2 block">⏰ Janela do GAM (horário de São Paulo)</label>
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                          <span className="text-xs text-muted-foreground">GAM das</span>
                          <select className="bg-background border border-border rounded-md px-2 py-1.5 text-sm font-mono"
                            value={gStart}
                            onChange={(e) => patchDp({ gam_window: { ...(dp.gam_window ?? {}), start: Number(e.target.value) } })}>
                            {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{hh(i)}</option>)}
                          </select>
                          <span className="text-xs text-muted-foreground">até</span>
                          <select className="bg-background border border-border rounded-md px-2 py-1.5 text-sm font-mono"
                            value={gEnd}
                            onChange={(e) => patchDp({ gam_window: { ...(dp.gam_window ?? {}), end: Number(e.target.value) } })}>
                            {Array.from({ length: 25 }, (_, i) => <option key={i} value={i}>{i === 24 ? '24:00 (meia-noite)' : hh(i)}</option>)}
                          </select>
                        </div>
                        {/* Timeline visual de 24h */}
                        <div className="flex gap-[2px] mb-1">
                          {Array.from({ length: 24 }, (_, h) => {
                            const g = inGam(h)
                            const isNow = h === nowH
                            return (
                              <div key={h} className="flex-1 flex flex-col items-center gap-0.5" title={`${hh(h)} — ${g ? 'GAM' : 'Adskeeper'}`}>
                                <div className={`w-full h-6 rounded-sm ${g ? 'bg-emerald-500' : 'bg-blue-500'} ${isNow ? 'ring-2 ring-amber-400 ring-offset-1 ring-offset-background' : ''}`} />
                                {h % 6 === 0 && <span className="text-[8px] text-muted-foreground">{h}h</span>}
                              </div>
                            )
                          })}
                        </div>
                        <div className="flex items-center gap-3 text-[11px] mt-2">
                          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" /> GAM</span>
                          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-blue-500" /> Adskeeper</span>
                          <span className="flex items-center gap-1 text-amber-500"><span className="inline-block w-3 h-3 rounded-sm ring-2 ring-amber-400" /> agora ({hh(nowH)})</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-2">
                          Agora ({hh(nowH)}) está servindo <strong className={inGam(nowH) ? 'text-emerald-500' : 'text-blue-500'}>{inGam(nowH) ? 'GAM' : 'Adskeeper'}</strong>. {gStart === gEnd && 'início = fim → sempre Adskeeper. '}A barra mostra as 24h: verde = GAM, azul = Adskeeper.
                        </p>
                      </div>
                    )
                  })()}

                  {/* GAM */}
                  <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-2">
                    <h4 className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">🟢 GAM</h4>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Ad unit</label>
                      <Input className="font-mono text-xs" placeholder="/22639388115/seusite"
                        value={dp.gam?.ad_unit ?? ''}
                        onChange={(e) => patchDp({ gam: { ...(dp.gam ?? {}), ad_unit: e.target.value } })} />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Clique forçado (passthrough) %</label>
                      <Input type="number" min={0} max={100} className="w-24"
                        value={dp.gam?.passthrough ?? 0}
                        onChange={(e) => patchDp({ gam: { ...(dp.gam ?? {}), passthrough: Math.max(0, Math.min(100, Number(e.target.value))) } })} />
                      <p className="text-[11px] text-muted-foreground mt-1">
                        <strong>0% = display passivo</strong> (recomendado). <span className="text-amber-500">⚠️ Clique forçado no GAM/AdSense fere as políticas do Google e pode banir a conta — use por sua conta e risco.</span>
                      </p>
                    </div>
                  </div>

                  {/* Adskeeper */}
                  <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4 space-y-2">
                    <h4 className="text-sm font-semibold text-blue-600 dark:text-blue-400">🔵 Adskeeper (com passthrough)</h4>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Loader (vai no &lt;head&gt;, 1x)</label>
                      <textarea rows={2} className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono"
                        placeholder='<script src="https://jsc.adskeeper.com/site/XXXXX.js" async></script>'
                        value={dp.adskeeper?.loader ?? ''}
                        onChange={(e) => patchDp({ adskeeper: { ...(dp.adskeeper ?? {}), loader: e.target.value } })} />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Widget (vai no &lt;body&gt;)</label>
                      <textarea rows={3} className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono"
                        placeholder='<div data-type="_mgwidget" data-widget-id="XXXXX"></div><script>...</script>'
                        value={dp.adskeeper?.widget ?? ''}
                        onChange={(e) => patchDp({ adskeeper: { ...(dp.adskeeper ?? {}), widget: e.target.value } })} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-muted-foreground">Clique forçado (passthrough)</label>
                      <Input type="number" min={0} max={100} className="w-20"
                        value={dp.adskeeper?.passthrough ?? 60}
                        onChange={(e) => patchDp({ adskeeper: { ...(dp.adskeeper ?? {}), passthrough: Number(e.target.value) } })} />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                  </div>

                  {/* Cap */}
                  <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <button type="button" onClick={() => patchDp({ cap: { ...(dp.cap ?? {}), enabled: !dp.cap?.enabled } })}
                        className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${dp.cap?.enabled ? 'bg-amber-500' : 'bg-muted'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${dp.cap?.enabled ? 'translate-x-4' : ''}`} />
                      </button>
                      <span className="text-sm font-medium">💰 Trocar pra Adskeeper ao bater o teto do GAM</span>
                    </label>
                    {dp.cap?.enabled && (
                      <div className="flex items-center gap-2 flex-wrap ml-11">
                        <span className="text-xs text-muted-foreground">Teto US$</span>
                        <Input type="number" min={0} className="w-24" value={dp.cap?.gam_daily_usd ?? 60}
                          onChange={(e) => patchDp({ cap: { ...(dp.cap ?? {}), gam_daily_usd: Number(e.target.value) } })} />
                        <span className="text-xs text-muted-foreground">/dia · eCPM US$</span>
                        <Input type="number" min={0} className="w-24" value={dp.cap?.gam_ecpm_usd ?? 28}
                          onChange={(e) => patchDp({ cap: { ...(dp.cap ?? {}), gam_ecpm_usd: Number(e.target.value) } })} />
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground ml-11">Estimativa por pageview. Ao atingir, serve Adskeeper o resto do dia.</p>
                  </div>

                  {/* CTR breaker — proteção automática anti-bloqueio */}
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <button type="button" onClick={() => patchDp({ ctr_breaker: { ...(dp.ctr_breaker ?? {}), enabled: !dp.ctr_breaker?.enabled } })}
                        className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${dp.ctr_breaker?.enabled ? 'bg-amber-500' : 'bg-muted'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${dp.ctr_breaker?.enabled ? 'translate-x-4' : ''}`} />
                      </button>
                      <span className="text-sm font-medium">🛡️ Trocar pra Adskeeper se a taxa de cliques do Google subir demais</span>
                    </label>
                    <p className="text-[11px] text-muted-foreground ml-11">
                      Proteção automática: se a taxa de cliques (CTR) da sua conta do Google ficar alta demais, o sistema tira o Google do overlay e usa só o Adskeeper, até a taxa baixar. Checa de hora em hora. <strong>O resto do site não muda.</strong>
                    </p>
                    {dp.ctr_breaker?.enabled && (
                      <div className="ml-11 space-y-3 pt-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground">Tirar o Google quando a taxa de cliques passar de</span>
                          <Input type="number" min={1} max={100} step="0.5" className="w-20"
                            value={dp.ctr_breaker?.max_ctr_pct ?? 35}
                            onChange={(e) => patchDp({ ctr_breaker: { ...(dp.ctr_breaker ?? {}), max_ctr_pct: Number(e.target.value) } })} />
                          <span className="text-xs text-muted-foreground">%</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground">Voltar a usar o Google quando cair abaixo de</span>
                          <Input type="number" min={0} max={100} step="0.5" className="w-20"
                            value={dp.ctr_breaker?.resume_ctr_pct ?? 25}
                            onChange={(e) => patchDp({ ctr_breaker: { ...(dp.ctr_breaker ?? {}), resume_ctr_pct: Number(e.target.value) } })} />
                          <span className="text-xs text-muted-foreground">%</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          💡 Exemplo: deixe <strong>tirar em 35%</strong> e <strong>voltar em 25%</strong>. Se 35 de cada 100 visitantes clicarem no anúncio do Google, o sistema troca pro Adskeeper; quando voltar pra menos de 25, religa o Google. Use números diferentes pros dois pra não ficar ligando e desligando toda hora.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </details>

          {/* ⚙️ Avançado */}
          <details className="rounded-xl border border-border bg-card">
            <summary className="p-4 cursor-pointer text-sm font-semibold flex items-center gap-2 select-none"><span>⚙️</span> Avançado</summary>
            <div className="px-4 pb-4 space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">ID do AdSense (opcional)</label>
                <Input value={(cfg as any).adsense_client ?? ''} onChange={(e) => setCfg((p) => ({ ...p, adsense_client: e.target.value }))} placeholder="ca-pub-XXXXXXXX (detectado automaticamente se vazio)" className="font-mono text-xs" />
                <p className="text-[11px] text-muted-foreground mt-1">Detectado automaticamente da página. Preencha só se a detecção falhar.</p>
              </div>
            </div>
          </details>

          {/* Templates */}
          <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <h3 className="text-sm font-semibold">Templates</h3>
            <div className="grid grid-cols-2 gap-2">
              {([
                {
                  id: 'player_yt',
                  label: 'Player YouTube',
                  desc: 'Video loading + texto + CTA',
                  cfg: {
                    permanent: false, auto_dismiss: 0, ad_protect: false, close_delay: 3, bg_color: '#000000',
                    blocks: [
                      { type: 'video', enabled: true, title: 'Preparando seu vídeo', subtitle: 'Conteúdo exclusivo para você', thumbnail: '', duration: 8, accent_color: '#ff0000' },
                      { type: 'typing', enabled: true, messages: ['Oi! Vi que você veio do Instagram...', 'Tenho algo especial pra te mostrar'], speed: 40, delay_between: 800 },
                      { type: 'counter', enabled: true, base_number: 2847, variation: 50, text: 'pessoas vendo agora' },
                      { type: 'cta', enabled: true, text: 'Assistir Agora →', url: '', color: '#ff0000', hint: 'Toque para continuar', delay: 0 },
                    ] as OverlayBlock[],
                  },
                },
                {
                  id: 'guia_funil',
                  label: 'Guia / Funil',
                  desc: 'Player com loading → play → revela anúncio',
                  cfg: {
                    permanent: true, auto_dismiss: 0, ad_protect: true, close_delay: 3, bg_color: '#000000',
                    blocks: [
                      { type: 'text', enabled: true, content: 'CARREGANDO VIDEO...', size: 'xl' as const, color: '#ff4444', bold: true, uppercase: true, align: 'center' as const },
                      { type: 'text', enabled: true, content: 'VEJA MEU CONTEÚDO COMPLETO. CLIQUE NA FOTO ABAIXO 👇', size: 'md' as const, color: '#e30203', bold: true, uppercase: false, align: 'center' as const },
                      { type: 'ad_slot', enabled: true, template: 'video', code: '', thumbnail: '', title: 'Carregando vídeo...', duration: 8, accent_color: '#e30203', status_messages: ['Preparando conexão segura...', 'Otimizando buffer...', 'Verificando região...', 'Pronto!'], micro_commit_text: 'Liberando acesso...', micro_commit_delay: 2500, play_text: '▶ ASSISTIR AGORA', wpp_contact_name: '', wpp_header_text: '' },
                      { type: 'text', enabled: true, content: 'PARA VER O VÍDEO COMPLETO, CLIQUE NA IMAGEM ACIMA 👆', size: 'md' as const, color: '#e30203', bold: true, uppercase: false, align: 'center' as const },
                      { type: 'text', enabled: true, content: 'AGUARDE, VÍDEO CARREGANDO...', size: 'md' as const, color: '#ffffff', bold: false, uppercase: false, align: 'center' as const },
                    ] as OverlayBlock[],
                  },
                },
                {
                  id: 'video_premium',
                  label: '🎬 Vídeo Premium',
                  desc: 'Skeleton shimmer + micro-commitment + ad passthrough',
                  cfg: {
                    permanent: true, auto_dismiss: 0, ad_protect: true, close_delay: 3, bg_color: '#0b0e14', max_views: 1, cooldown_hours: 12, count_on: 'click' as const,
                    blocks: [
                      { type: 'text', enabled: true, content: 'CONTEÚDO EXCLUSIVO', size: 'lg' as const, color: '#ff0050', bold: true, uppercase: true, align: 'center' as const },
                      { type: 'ad_slot', enabled: true, template: 'video', code: '', thumbnail: '', title: 'Preparando conexão segura...', duration: 6, accent_color: '#ff0050', status_messages: ['Preparando conexão segura...', 'Otimizando buffer...', 'Verificando região...', 'Pronto!'], micro_commit_text: 'Liberando acesso...', micro_commit_delay: 2500, play_text: '▶ ASSISTIR AGORA', wpp_contact_name: '', wpp_header_text: '' },
                      { type: 'text', enabled: true, content: 'Toque no play para assistir', size: 'sm' as const, color: '#ffffff', bold: false, uppercase: false, align: 'center' as const },
                    ] as OverlayBlock[],
                  },
                },
                {
                  id: 'whatsapp',
                  label: '💬 WhatsApp',
                  desc: 'Verificação de contato + micro-commitment + ad',
                  cfg: {
                    permanent: true, auto_dismiss: 0, ad_protect: true, close_delay: 3, bg_color: '#ECE5DD', max_views: 1, cooldown_hours: 12, count_on: 'click' as const,
                    blocks: [
                      { type: 'ad_slot', enabled: true, template: 'whatsapp', code: '', thumbnail: '', title: 'Buscando contato...', duration: 5, accent_color: '#25D366', status_messages: ['Buscando contato...', 'Verificando disponibilidade...', 'Validando conexão...', 'Contato encontrado!'], micro_commit_text: 'Redirecionando para o App...', micro_commit_delay: 2000, play_text: '🟢 INICIAR CONVERSA AGORA', wpp_contact_name: 'Consultoria Premium', wpp_header_text: 'Conexão Segura' },
                    ] as OverlayBlock[],
                  },
                },
                {
                  id: 'security_gate',
                  label: '🔒 Security Gate',
                  desc: 'Verificação de segurança + ad visível + external verify',
                  cfg: {
                    permanent: true, auto_dismiss: 0, ad_protect: true, close_delay: 3, bg_color: '#09090b', max_views: 1, cooldown_hours: 12, count_on: 'click' as const,
                    blocks: [
                      { type: 'ad_slot', enabled: true, template: 'security_gate', code: '', thumbnail: '', title: 'Analisando segurança...', duration: 5, accent_color: '#ffffff', status_messages: ['Analisando segurança da conexão...', 'Otimizando leitura mobile...', 'Verificando região...', 'Acesso quase pronto...'], micro_commit_text: '', micro_commit_delay: 0, play_text: 'CONTINUAR PARA O CONTEÚDO →', wpp_contact_name: '', wpp_header_text: '', wpp_avatar_url: '', gate_label: 'SECURITY CHECK', gate_messages: ['Analisando segurança da conexão...', 'Otimizando leitura mobile...', 'Verificando região...', 'Acesso quase pronto...'], gate_message_interval: 1500, gate_hint: 'Ao continuar, você confirma ser um leitor humano', button_style: 'filled' as const, external_verify: true, verify_time: [7, 9], verify_message: 'Validando interação... aguarde', verify_early_message: 'Interação não concluída. Valide o acesso na página aberta.', fallback_url: '', dd_messages: ['Preparando conteúdo...', 'Quase lá...'], dd_images: [] },
                    ] as OverlayBlock[],
                  },
                },
                {
                  id: 'simples',
                  label: '📦 Simples',
                  desc: 'Texto + anúncio direto, sem player',
                  cfg: {
                    permanent: true, auto_dismiss: 0, ad_protect: false, close_delay: 0, bg_color: '#000000', max_views: 8, cooldown_hours: 3, count_on: 'view' as const,
                    blocks: [
                      { type: 'text', enabled: true, content: 'CARREGANDO VIDEO...', size: 'xl' as const, color: '#e30203', bold: true, uppercase: true, align: 'center' as const },
                      { type: 'text', enabled: true, content: 'VEJA MEU CONTEÚDO COMPLETO. CLIQUE NA FOTO ABAIXO 👇', size: 'md' as const, color: '#e30203', bold: true, uppercase: false, align: 'center' as const },
                      { type: 'ad_slot', enabled: true, template: 'raw', code: '', thumbnail: '', title: '', duration: 2, accent_color: '#e30203', ad_count: '1', ad_provider: 'groone', status_messages: [], play_text: '', header_text: '', tap_text: '', wpp_contact_name: '', wpp_header_text: '' },
                      { type: 'text', enabled: true, content: 'PARA VER O VÍDEO COMPLETO, CLIQUE NA IMAGEM ACIMA 👆', size: 'md' as const, color: '#e30203', bold: true, uppercase: false, align: 'center' as const },
                      { type: 'text', enabled: true, content: 'AGUARDE, VÍDEO CARREGADO...', size: 'md' as const, color: '#ffffff', bold: false, uppercase: false, align: 'center' as const },
                    ] as OverlayBlock[],
                  },
                },
                {
                  id: 'content_ab',
                  label: '📰 Artigo + A/B (anti-ban)',
                  desc: 'Metade vê artigo passivo, metade vê vídeo forçado',
                  cfg: {
                    permanent: true, auto_dismiss: 0, ad_protect: true, close_delay: 3, bg_color: '#000000', max_views: 3, cooldown_hours: 12, count_on: 'click' as const,
                    blocks: [
                      { type: 'ad_slot', enabled: true, template: 'content',
                        ab_forced_pct: 50, ab_forced_template: 'raw', ab_passive_template: 'content',
                        title: 'Veja como milhares de pessoas estão aproveitando isso em 2026',
                        paragraphs: [
                          'Edite este texto com o conteúdo do seu artigo. Quanto mais natural e útil, mais o anúncio passa despercebido como recomendação.',
                          'Escreva alguns parágrafos curtos. O primeiro widget aparece no topo, e o segundo (se você configurar) no meio do texto.',
                          'No modo passivo não há clique forçado — o leitor clica no anúncio só se quiser, o que mantém o CTR orgânico e protege a conta.',
                        ],
                        widget_feed: '',
                        fake_likes: 865, fake_comments: 216, fake_shares: 288,
                        accent_color: '#e30203', show_loading: true, show_play_button: true, show_video_bar: true,
                        play_text: 'TOQUE PARA ASSISTIR', text_top: '👇 ASSISTA O VÍDEO LOGO ABAIXO 👇', text_bottom: '☝️ TOQUE NA IMAGEM ACIMA PARA ASSISTIR ☝️', fake_duration: '3:42',
                        code: '', thumbnail: '', thumbnails: [], duration: 6, status_messages: [], wpp_contact_name: '', wpp_header_text: '' },
                    ] as OverlayBlock[],
                  },
                },
              ] as const).map((tpl) => (
                <button
                  key={tpl.id}
                  onClick={() => setCfg((p) => ({ ...p, ...tpl.cfg, enabled: true, version: 2 }))}
                  className="text-left rounded-lg border border-border p-3 hover:border-primary/50 hover:bg-muted/20 transition-colors group overflow-hidden"
                >
                  {/* Animated preview */}
                  {tpl.id === 'player_yt' && (
                    <div className="mb-2 rounded-md overflow-hidden" style={{ height: 64, background: '#0f0f0f', position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 4 }}>
                        <div style={{ width: 16, height: 16, border: '2px solid #fff', borderTopColor: '#f00', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                        <div style={{ fontSize: 7, color: 'rgba(255,255,255,.5)' }}>Preparando vídeo...</div>
                      </div>
                      <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 2, background: '#333' }}>
                        <div style={{ height: '100%', width: '60%', background: '#f00', animation: 'tplbar 3s ease-in-out infinite' }} />
                      </div>
                    </div>
                  )}
                  {tpl.id === 'guia_funil' && (
                    <div className="mb-2 rounded-md overflow-hidden" style={{ height: 64, background: '#000', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 3 }}>
                      <div style={{ fontSize: 7, color: '#e30203', fontWeight: 700, letterSpacing: 1 }}>CARREGANDO VIDEO...</div>
                      <div style={{ width: '70%', height: 18, background: '#1a1a1a', borderRadius: 4, border: '1px solid #e30203', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 10, height: 10, border: '2px solid #fff', borderTopColor: '#e30203', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
                      </div>
                      <div style={{ fontSize: 6, color: '#e30203' }}>CLIQUE NA FOTO ACIMA</div>
                    </div>
                  )}
                  {tpl.id === 'video_premium' && (
                    <div className="mb-2 rounded-md overflow-hidden" style={{ height: 64, background: '#0b0e14', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 4 }}>
                      <div style={{ width: '65%', height: 28, borderRadius: 6, background: 'linear-gradient(135deg,#1a1a2e,#16213e)', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent 0%,rgba(255,255,255,.06) 50%,transparent 100%)', animation: 'tplshimmer 2s ease-in-out infinite' }} />
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 12, height: 12, borderRadius: '50%', background: 'rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 6, color: 'rgba(255,255,255,.4)' }}>&#9654;</span>
                        </div>
                      </div>
                      <div style={{ width: '65%', height: 2, background: 'rgba(255,255,255,.06)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: 'linear-gradient(90deg,transparent,#ff0050)', animation: 'tplbar 4s cubic-bezier(.4,0,.2,1) infinite' }} />
                      </div>
                      <div style={{ fontSize: 6, color: 'rgba(255,255,255,.35)', animation: 'tplpulse 2s ease-in-out infinite' }}>Otimizando buffer...</div>
                    </div>
                  )}
                  {tpl.id === 'whatsapp' && (
                    <div className="mb-2 rounded-md overflow-hidden" style={{ height: 64, background: '#ECE5DD', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ background: '#075E54', padding: '3px 6px', display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                        <span style={{ fontSize: 6, color: '#fff', fontWeight: 600 }}>Conexão Segura</span>
                        <span style={{ fontSize: 5, color: 'rgba(255,255,255,.5)', marginLeft: 'auto' }}>criptografado</span>
                      </div>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#c4ccd4', position: 'relative', overflow: 'hidden' }}>
                          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent 0%,rgba(255,255,255,.3) 50%,transparent 100%)', animation: 'tplshimmer 1.8s ease-in-out infinite' }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <div style={{ width: 40, height: 5, borderRadius: 3, background: 'linear-gradient(90deg,#d4d4d4,#e8e8e8,#d4d4d4)', backgroundSize: '200% 100%', animation: 'tplskel 1.5s ease-in-out infinite' }} />
                          <div style={{ width: 28, height: 4, borderRadius: 2, background: 'linear-gradient(90deg,#d4d4d4,#e8e8e8,#d4d4d4)', backgroundSize: '200% 100%', animation: 'tplskel 1.5s ease-in-out infinite .2s' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  {tpl.id === 'security_gate' && (
                    <div className="mb-2 rounded-md overflow-hidden" style={{ height: 64, background: '#09090b', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      <div style={{ position: 'absolute', width: 8, height: 8, top: 6, left: 6, borderLeft: '1px solid rgba(255,255,255,.06)', borderTop: '1px solid rgba(255,255,255,.06)' }} />
                      <div style={{ position: 'absolute', width: 8, height: 8, top: 6, right: 6, borderRight: '1px solid rgba(255,255,255,.06)', borderTop: '1px solid rgba(255,255,255,.06)' }} />
                      <div style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,.1)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                      <div style={{ fontSize: 5, color: 'rgba(255,255,255,.25)', textTransform: 'uppercase' as const, letterSpacing: 2 }}>SECURITY CHECK</div>
                      <div style={{ display: 'flex', gap: 2 }}>
                        {[0,1,2,3].map(i => <div key={i} style={{ width: 14, height: 1.5, borderRadius: 1, background: i < 2 ? '#fff' : 'rgba(255,255,255,.06)' }} />)}
                      </div>
                    </div>
                  )}
                  {tpl.id === 'content_ab' && (
                    <div className="mb-2 rounded-md overflow-hidden" style={{ height: 64, background: '#fff', position: 'relative', display: 'flex' }}>
                      {/* lado artigo (passivo) */}
                      <div style={{ flex: 1, padding: '4px 5px', display: 'flex', flexDirection: 'column', gap: 2, borderRight: '1px dashed #ddd' }}>
                        <div style={{ fontSize: 4.5, color: '#9aa0a6', fontWeight: 700, letterSpacing: .5 }}>INTERESSANTE PARA VOCÊ</div>
                        <div style={{ height: 14, borderRadius: 2, background: 'linear-gradient(90deg,#eee,#f6f6f6,#eee)', backgroundSize: '200% 100%', animation: 'tplskel 1.5s ease-in-out infinite' }} />
                        <div style={{ display: 'flex', gap: 4, fontSize: 5, color: '#65676b' }}><span>👍865</span><span>💬216</span></div>
                        <div style={{ height: 2, width: '90%', background: '#eee', borderRadius: 2 }} />
                        <div style={{ height: 2, width: '75%', background: '#eee', borderRadius: 2 }} />
                      </div>
                      {/* lado vídeo (forçado) */}
                      <div style={{ flex: 1, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
                        <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#e30203', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 7, color: '#fff' }}>&#9654;</span>
                        </div>
                        <div style={{ fontSize: 4.5, color: '#e30203', fontWeight: 700 }}>TOQUE PARA ASSISTIR</div>
                      </div>
                    </div>
                  )}
                  <div className="text-sm font-medium">{tpl.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{tpl.desc}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Clicar num template substitui os blocos atuais.</p>
          </div>

          {/* Preview + Blocks side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-4">

          {/* Blocks */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold px-1 flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              Blocos
            </h3>
            {cfg.blocks.map((block, idx) => {
              const meta = BLOCK_META[block.type] || { label: block.type, desc: '' }
              const isOpen = expanded === idx
              return (
                <div key={block.type + '-' + idx} className="rounded-xl border border-border bg-card overflow-hidden">
                  {/* Block header */}
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/20 transition-colors"
                    onClick={() => setExpanded(isOpen ? null : idx)}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        updateBlock(idx, { enabled: !block.enabled })
                      }}
                      className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${block.enabled ? 'bg-emerald-500' : 'bg-muted'}`}
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.enabled ? 'translate-x-4' : ''}`}
                      />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{meta.label}</div>
                      <div className="text-xs text-muted-foreground truncate">{meta.desc}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          moveBlock(idx, -1)
                        }}
                        className="p-1 rounded hover:bg-muted/50 text-muted-foreground disabled:opacity-30"
                        disabled={idx === 0}
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          moveBlock(idx, 1)
                        }}
                        className="p-1 rounded hover:bg-muted/50 text-muted-foreground disabled:opacity-30"
                        disabled={idx === cfg.blocks.length - 1}
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Block config fields */}
                  {isOpen && (
                    <div className="border-t border-border p-3 space-y-3">
                      {block.type === 'video' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Título</label>
                            <Input value={(block as VideoBlock).title} onChange={(e) => updateBlock(idx, { title: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Subtítulo</label>
                            <Input value={(block as VideoBlock).subtitle} onChange={(e) => updateBlock(idx, { subtitle: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Thumbnail</label>
                            <ImageUrlField value={(block as VideoBlock).thumbnail} onChange={(url) => updateBlock(idx, { thumbnail: url })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Duração (s)</label>
                            <Input type="number" min={2} max={30} value={(block as VideoBlock).duration} onChange={(e) => updateBlock(idx, { duration: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Cor destaque</label>
                            <div className="flex gap-2 items-center">
                              <input type="color" value={(block as VideoBlock).accent_color} onChange={(e) => updateBlock(idx, { accent_color: e.target.value })} className="w-8 h-8 rounded border border-border cursor-pointer" />
                              <Input value={(block as VideoBlock).accent_color} onChange={(e) => updateBlock(idx, { accent_color: e.target.value })} className="flex-1" />
                            </div>
                          </div>
                        </div>
                      )}

                      {block.type === 'typing' && (
                        <div className="space-y-3">
                          <label className="text-xs text-muted-foreground block">Mensagens</label>
                          {(block as TypingBlock).messages.map((msg, mi) => (
                            <div key={mi} className="flex gap-2">
                              <Input
                                value={msg}
                                onChange={(e) =>
                                  updateBlock(idx, { messages: (block as TypingBlock).messages.map((m, j) => (j === mi ? e.target.value : m)) })
                                }
                                className="flex-1"
                                placeholder={`Mensagem ${mi + 1}`}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => updateBlock(idx, { messages: (block as TypingBlock).messages.filter((_, j) => j !== mi) })}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                          <Button size="sm" variant="outline" onClick={() => updateBlock(idx, { messages: [...(block as TypingBlock).messages, ''] })}>
                            <Plus className="h-3 w-3 mr-1" />
                            Mensagem
                          </Button>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Velocidade (ms/char)</label>
                              <Input type="number" min={10} max={200} value={(block as TypingBlock).speed} onChange={(e) => updateBlock(idx, { speed: Number(e.target.value) })} />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Delay entre msgs (ms)</label>
                              <Input type="number" min={0} value={(block as TypingBlock).delay_between} onChange={(e) => updateBlock(idx, { delay_between: Number(e.target.value) })} />
                            </div>
                          </div>
                        </div>
                      )}

                      {block.type === 'image' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="md:col-span-2">
                            <label className="text-xs text-muted-foreground mb-1 block">Imagem</label>
                            <ImageUrlField value={(block as ImageBlock).url} onChange={(url) => updateBlock(idx, { url })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Aspect ratio</label>
                            <Input value={(block as ImageBlock).aspect_ratio} onChange={(e) => updateBlock(idx, { aspect_ratio: e.target.value })} placeholder="16/9" />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Border radius (px)</label>
                            <Input type="number" min={0} value={(block as ImageBlock).border_radius} onChange={(e) => updateBlock(idx, { border_radius: Number(e.target.value) })} />
                          </div>
                        </div>
                      )}

                      {block.type === 'counter' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Número base</label>
                            <Input type="number" min={0} value={(block as CounterBlock).base_number} onChange={(e) => updateBlock(idx, { base_number: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Variação (±)</label>
                            <Input type="number" min={0} value={(block as CounterBlock).variation} onChange={(e) => updateBlock(idx, { variation: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Texto</label>
                            <Input value={(block as CounterBlock).text} onChange={(e) => updateBlock(idx, { text: e.target.value })} />
                          </div>
                        </div>
                      )}

                      {block.type === 'timer' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Minutos</label>
                            <Input type="number" min={1} max={60} value={(block as TimerBlock).minutes} onChange={(e) => updateBlock(idx, { minutes: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Label</label>
                            <Input value={(block as TimerBlock).label} onChange={(e) => updateBlock(idx, { label: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Ao zerar</label>
                            <select
                              value={(block as TimerBlock).on_zero}
                              onChange={(e) => updateBlock(idx, { on_zero: e.target.value })}
                              className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                            >
                              <option value="freeze">Travar em 00:01</option>
                              <option value="redirect">Redirecionar (CTA url)</option>
                              <option value="hide">Esconder timer</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {block.type === 'social_proof' && (
                        <div className="space-y-3">
                          <label className="text-xs text-muted-foreground block">Depoimentos</label>
                          {(block as SocialProofBlock).testimonials.map((t, ti) => (
                            <div key={ti} className="flex gap-2 items-start">
                              <Input
                                value={t.name}
                                onChange={(e) =>
                                  updateBlock(idx, {
                                    testimonials: (block as SocialProofBlock).testimonials.map((x, j) => (j === ti ? { ...x, name: e.target.value } : x)),
                                  })
                                }
                                className="w-28"
                                placeholder="Nome"
                              />
                              <Input
                                value={t.text}
                                onChange={(e) =>
                                  updateBlock(idx, {
                                    testimonials: (block as SocialProofBlock).testimonials.map((x, j) => (j === ti ? { ...x, text: e.target.value } : x)),
                                  })
                                }
                                className="flex-1"
                                placeholder="Depoimento"
                              />
                              <Input
                                type="number"
                                min={1}
                                max={5}
                                value={t.stars}
                                onChange={(e) =>
                                  updateBlock(idx, {
                                    testimonials: (block as SocialProofBlock).testimonials.map((x, j) =>
                                      j === ti ? { ...x, stars: Number(e.target.value) } : x
                                    ),
                                  })
                                }
                                className="w-16"
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  updateBlock(idx, {
                                    testimonials: (block as SocialProofBlock).testimonials.filter((_, j) => j !== ti),
                                  })
                                }
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateBlock(idx, {
                                testimonials: [...(block as SocialProofBlock).testimonials, { name: '', text: '', stars: 5 }],
                              })
                            }
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Depoimento
                          </Button>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Intervalo (ms)</label>
                            <Input
                              type="number"
                              min={1000}
                              value={(block as SocialProofBlock).interval}
                              onChange={(e) => updateBlock(idx, { interval: Number(e.target.value) })}
                              className="w-32"
                            />
                          </div>
                        </div>
                      )}

                      {block.type === 'text' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="md:col-span-2">
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                              <input type="checkbox" checked={!!(block as any).rich_mode} onChange={(e) => updateBlock(idx, { rich_mode: e.target.checked })} className="rounded" />
                              <span className="text-xs font-medium">Editor avançado de texto</span>
                            </label>
                          </div>
                          {(block as any).rich_mode ? (
                            <div className="md:col-span-2">
                              <label className="text-xs text-muted-foreground mb-1 block">Conteúdo</label>
                              <RichTextEditor
                                value={(block as any).rich_content ?? ''}
                                onChange={(html) => updateBlock(idx, { rich_content: html })}
                                outlineColor={(block as any).text_outline ?? 'none'}
                                onOutlineChange={(c) => updateBlock(idx, { text_outline: c })}
                              />
                            </div>
                          ) : (
                          <div className="md:col-span-2">
                            <label className="text-xs text-muted-foreground mb-1 block">Conteúdo</label>
                            <textarea
                              value={(block as TextBlock).content}
                              onChange={(e) => updateBlock(idx, { content: e.target.value })}
                              className="w-full min-h-[60px] rounded-md border border-border bg-background px-3 py-2 text-sm resize-y"
                              placeholder="Texto que aparece no overlay"
                            />
                          </div>
                          )}
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Tamanho</label>
                            <select
                              value={(block as TextBlock).size}
                              onChange={(e) => updateBlock(idx, { size: e.target.value })}
                              className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                            >
                              <option value="sm">Pequeno (12px)</option>
                              <option value="md">Médio (15px)</option>
                              <option value="lg">Grande (18px)</option>
                              <option value="xl">Extra grande (22px)</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Cor</label>
                            <div className="flex gap-2 items-center">
                              <input type="color" value={(block as TextBlock).color} onChange={(e) => updateBlock(idx, { color: e.target.value })} className="w-8 h-8 rounded border border-border cursor-pointer" />
                              <Input value={(block as TextBlock).color} onChange={(e) => updateBlock(idx, { color: e.target.value })} className="flex-1" />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Alinhamento</label>
                            <select
                              value={(block as TextBlock).align}
                              onChange={(e) => updateBlock(idx, { align: e.target.value })}
                              className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                            >
                              <option value="left">Esquerda</option>
                              <option value="center">Centro</option>
                              <option value="right">Direita</option>
                            </select>
                          </div>
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={(block as TextBlock).bold} onChange={(e) => updateBlock(idx, { bold: e.target.checked })} className="rounded" />
                              <span className="text-xs text-muted-foreground">Bold</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={(block as TextBlock).uppercase} onChange={(e) => updateBlock(idx, { uppercase: e.target.checked })} className="rounded" />
                              <span className="text-xs text-muted-foreground">MAIÚSCULO</span>
                            </label>
                          </div>
                        </div>
                      )}

                      {block.type === 'ad_slot' && (() => {
                        const b = block as AdSlotBlock;
                        const isWpp = b.template === 'whatsapp';
                        const isSG = b.template === 'security_gate';
                        return (
                          <div className="space-y-3">
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Template</label>
                              <select
                                value={b.template ?? 'video'}
                                onChange={(e) => {
                                  const tpl = e.target.value as 'video' | 'whatsapp' | 'security_gate' | 'raw' | 'content' | 'rewarded';
                                  const defaults = tpl === 'content'
                                    ? { template: tpl, title: 'Como conseguir um cartão de crédito aprovado mesmo com o nome negativado em 2026', paragraphs: [], ab_forced_pct: 50, ab_forced_template: 'raw', ab_passive_template: 'content', widget_feed: '', fake_likes: 865, fake_comments: 216, fake_shares: 288 }
                                    : tpl === 'rewarded'
                                    ? { template: tpl, accent_color: '#6366f1', rw_logo: 'Conteúdo+', rw_title: 'Conteúdo exclusivo liberado', rw_subtitle: 'Veja um anúncio rápido de poucos segundos para continuar.', rw_btn: '▶ EXIBIR ANÚNCIO', rw_hint: 'Você libera o conteúdo assim que o anúncio terminar.', rw_ad_label: 'Publicidade', rw_wait: 'Aguarde o anúncio terminar…', rw_continue: 'CONTINUAR →', rw_done: '✓ Liberado! Toque para continuar', rw_timer: 5, rw_bg: '#f4f5f7', cta_url: '' }
                                    : tpl === 'whatsapp'
                                    ? { template: tpl, accent_color: '#25D366', title: 'Buscando contato...', status_messages: ['Buscando contato...', 'Verificando disponibilidade...', 'Validando conexão...', 'Contato encontrado!'], micro_commit_text: 'Redirecionando para o App...', micro_commit_delay: 2000, play_text: '🟢 INICIAR CONVERSA AGORA', wpp_contact_name: b.wpp_contact_name || 'Consultoria Premium', wpp_header_text: b.wpp_header_text || 'Conexão Segura' }
                                    : tpl === 'security_gate'
                                    ? { template: tpl, accent_color: '#ffffff', title: 'Analisando segurança...', play_text: 'CONTINUAR PARA O CONTEÚDO →', gate_label: 'SECURITY CHECK', gate_messages: ['Analisando segurança da conexão...', 'Otimizando leitura mobile...', 'Verificando região...', 'Acesso quase pronto...'], gate_message_interval: 1500, gate_hint: 'Ao continuar, você confirma ser um leitor humano', button_style: 'filled', external_verify: true, verify_time: [7, 9], verify_message: 'Validando interação... aguarde', verify_early_message: 'Interação não concluída. Valide o acesso na página aberta.', fallback_url: '', dd_messages: ['Preparando conteúdo...', 'Quase lá...'], dd_images: [], gate_images: [], micro_commit_text: '', micro_commit_delay: 0 }
                                    : tpl === 'raw'
                                    ? { template: tpl, accent_color: '#e30203', title: '', ad_count: '1', ad_provider: 'groone' }
                                    : { template: tpl, accent_color: '#ff0050', title: 'Carregando vídeo...', status_messages: ['Preparando conexão segura...', 'Otimizando buffer...', 'Verificando região...', 'Pronto!'], micro_commit_text: 'Liberando acesso...', micro_commit_delay: 2500, play_text: '▶ ASSISTIR AGORA' };
                                  updateBlock(idx, defaults);
                                }}
                                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                              >
                                <option value="video">🎬 Vídeo (Player fake)</option>
                                <option value="whatsapp">💬 WhatsApp (Verificação de contato)</option>
                                <option value="security_gate">🔒 Security Gate (Verificação + Ad visível)</option>
                                <option value="raw">📦 Simples (Só o anúncio, sem template)</option>
                                <option value="content">📰 Conteúdo (artigo + native ad passivo — anti-ban)</option>
                                <option value="rewarded">🎁 Rewarded (gate: vê anúncio por tempo → libera — anti-ban)</option>
                              </select>
                            </div>

                            <div className={`rounded-md ${isWpp ? 'bg-green-500/10 border-green-500/20' : isSG ? 'bg-blue-500/10 border-blue-500/20' : b.template === 'raw' ? 'bg-orange-500/10 border-orange-500/20' : 'bg-emerald-500/10 border-emerald-500/20'} border p-3 text-xs ${isWpp ? 'text-green-400' : isSG ? 'text-blue-400' : b.template === 'raw' ? 'text-orange-400' : 'text-emerald-400'} space-y-1`}>
                              {isSG ? (
                                <>
                                  <p><strong>Security Gate:</strong> Overlay dark minimal com spinner + ad visível. Captura automática da URL do anúncio.</p>
                                  <p>Lead clica → abre ad em nova aba → focus listener valida interação → libera conteúdo.</p>
                                </>
                              ) : isWpp ? (
                                <>
                                  <p><strong>WhatsApp:</strong> Simula verificação de contato. Header verde com cadeado, avatar com shimmer, status dinâmico.</p>
                                  <p>O lead toca "Iniciar conversa" → micro-commitment → ad atrás é exposto.</p>
                                </>
                              ) : b.template === 'raw' ? (
                                <>
                                  <p><strong>Simples:</strong> Loading (spinner + barra + mensagens) por cima do anúncio. Viewability conta desde o início.</p>
                                  <p>Após o tempo configurado, loading some e revela só o anúncio.</p>
                                </>
                              ) : b.template === 'content' ? (
                                <>
                                  <p><strong>Conteúdo (anti-ban):</strong> Artigo real + native ad do Adskeeper em DISPLAY PASSIVO. Sem clique forçado, sem botão fake.</p>
                                  <p>CTR orgânico → a conta não queima (modelo sustentável, estilo tidelux). Usa o widget de "Rotação de redes".</p>
                                </>
                              ) : (
                                <>
                                  <p><strong>Player overlay:</strong> Skeleton com shimmer + loading premium. Barra cubic-bezier + status dinâmico.</p>
                                  <p>O lead toca "Assistir" → micro-commitment → ad atrás é exposto. AdSense auto-detectado.</p>
                                </>
                              )}
                            </div>

                            {/* Content template settings (A/B + artigo + 2º widget + prova social) */}
                            {b.template === 'content' && (() => {
                              const abPct = (b as any).ab_forced_pct ?? 0
                              const paras: string[] = Array.isArray((b as any).paragraphs) ? (b as any).paragraphs : []
                              const setParas = (list: string[]) => updateBlock(idx, { paragraphs: list })
                              return (
                              <div className="space-y-3">
                                {/* A/B slider */}
                                <div className="rounded-lg border border-purple-500/20 bg-purple-500/5 p-3 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-purple-400">🎲 Diluição de risco (A/B)</span>
                                    <span className="text-xs font-bold text-purple-300">{abPct}% forçado</span>
                                  </div>
                                  <input type="range" min={0} max={100} step={5} value={abPct}
                                    onChange={(e) => updateBlock(idx, { ab_forced_pct: Number(e.target.value), ab_forced_template: 'raw', ab_passive_template: 'content' })}
                                    className="w-full accent-purple-500" />
                                  <div className="text-[11px] text-muted-foreground space-y-0.5">
                                    <p>→ <strong>{abPct}%</strong> veem o <strong>VÍDEO FORÇADO</strong> 🎬 <span className="text-amber-500">(+ eCPM, + risco de ban)</span></p>
                                    <p>→ <strong>{100 - abPct}%</strong> veem o <strong>ARTIGO PASSIVO</strong> 📄 <span className="text-emerald-500">(+ seguro)</span></p>
                                    <p className="text-muted-foreground/60">0% = só artigo (estilo tidelux). 100% = só vídeo forçado.</p>
                                  </div>
                                </div>

                                {/* Vídeo forçado (lado A/B) — só aparece se há fatia forçada */}
                                {abPct > 0 && (
                                  <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3 space-y-3">
                                    <span className="text-xs font-semibold text-orange-400">🎬 Vídeo forçado (lado {abPct}%)</span>
                                    <div>
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={(b as any).show_loading !== false} onChange={(e) => updateBlock(idx, { show_loading: e.target.checked })} className="rounded" />
                                        <span className="text-xs font-medium">Loading (spinner + barra)</span>
                                      </label>
                                      <p className="text-[10px] text-muted-foreground/60 mt-1">Desligado = ad aparece direto, sem loading.</p>
                                    </div>
                                    <div>
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={(b as any).show_play_button === true} onChange={(e) => updateBlock(idx, { show_play_button: e.target.checked })} className="rounded" />
                                        <span className="text-xs font-medium">Botão Play</span>
                                      </label>
                                      <p className="text-[10px] text-muted-foreground/60 mt-1">Aparece após loading (ou direto se loading desligado). Cobre o ad.</p>
                                    </div>
                                    {(b as any).show_play_button === true && (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                          <label className="text-xs text-muted-foreground mb-1 block">Click passthrough (%)</label>
                                          <Input type="number" min={0} max={100} value={(b as any).click_passthrough ?? 50} onChange={(e) => updateBlock(idx, { click_passthrough: Number(e.target.value) })} />
                                          <p className="text-[10px] text-muted-foreground/60 mt-1">0% = sempre revela o ad. 100% = sempre clica no ad.</p>
                                        </div>
                                        <div>
                                          <label className="text-xs text-muted-foreground mb-1 block">Texto do botão Play</label>
                                          <Input value={(b as any).play_text ?? 'TOQUE PARA ASSISTIR'} onChange={(e) => updateBlock(idx, { play_text: e.target.value })} />
                                        </div>
                                      </div>
                                    )}
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Texto ACIMA do card</label>
                                      <Input value={(b as any).text_top ?? ''} onChange={(e) => updateBlock(idx, { text_top: e.target.value })} placeholder="👇 ASSISTA O VÍDEO LOGO ABAIXO 👇" />
                                      <p className="text-[10px] text-muted-foreground/60 mt-1">Indica o clique (público +45). Vazio = não mostra.</p>
                                    </div>
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Texto ABAIXO do card</label>
                                      <Input value={(b as any).text_bottom ?? ''} onChange={(e) => updateBlock(idx, { text_bottom: e.target.value })} placeholder="☝️ TOQUE NA IMAGEM ACIMA PARA ASSISTIR ☝️" />
                                    </div>
                                    <div>
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={(b as any).show_video_bar !== false} onChange={(e) => updateBlock(idx, { show_video_bar: e.target.checked })} className="rounded" />
                                        <span className="text-xs font-medium">Barra de player (vídeo pausado)</span>
                                      </label>
                                      <p className="text-[10px] text-muted-foreground/60 mt-1">Rodapé com ▶ 0:00 · progresso · duração. Parece um vídeo pausado.</p>
                                    </div>
                                    {(b as any).show_video_bar !== false && (
                                      <div>
                                        <label className="text-xs text-muted-foreground mb-1 block">Duração exibida</label>
                                        <Input value={(b as any).fake_duration ?? '3:42'} onChange={(e) => updateBlock(idx, { fake_duration: e.target.value })} placeholder="3:42" />
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Artigo */}
                                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-2">
                                  <span className="text-xs font-semibold text-emerald-400">📄 Artigo (lado passivo)</span>
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Título</label>
                                    <Input value={(b as any).title ?? ''} onChange={(e) => updateBlock(idx, { title: e.target.value })} placeholder="Título do artigo" />
                                  </div>
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Parágrafos</label>
                                    <div className="space-y-2">
                                      {paras.map((p, pi) => (
                                        <div key={pi} className="flex gap-2 items-start">
                                          <span className="text-[10px] text-muted-foreground/60 mt-2 w-4 text-right">{pi + 1}</span>
                                          <textarea value={p} rows={2}
                                            onChange={(e) => { const l = [...paras]; l[pi] = e.target.value; setParas(l) }}
                                            className="flex-1 text-xs rounded-md border border-border bg-background px-2 py-1.5 resize-y" />
                                          <button type="button" onClick={() => setParas(paras.filter((_, i) => i !== pi))}
                                            className="text-muted-foreground hover:text-rose-500 mt-1.5 text-sm">🗑</button>
                                        </div>
                                      ))}
                                      <button type="button" onClick={() => setParas([...paras, ''])}
                                        className="text-xs text-emerald-500 hover:text-emerald-400 font-medium">+ adicionar parágrafo</button>
                                    </div>
                                  </div>
                                </div>

                                {/* 2º widget (feed) */}
                                <div className="rounded-lg border border-border bg-card/50 p-3 space-y-1">
                                  <label className="text-xs font-semibold block">📰 2º anúncio (feed do meio) <span className="font-normal text-muted-foreground">— opcional</span></label>
                                  <textarea value={(b as any).widget_feed ?? ''} rows={2}
                                    onChange={(e) => updateBlock(idx, { widget_feed: e.target.value })}
                                    placeholder='<div data-type="_mgwidget" data-widget-id="..."></div><script>...</script>'
                                    className="w-full text-xs font-mono rounded-md border border-border bg-background px-2 py-1.5 resize-y" />
                                  <p className="text-[10px] text-muted-foreground/60">Cole o 2º widget do Adskeeper (formato feed/infinito). Vazio = só o widget do topo.</p>
                                </div>

                                {/* Prova social */}
                                <div className="rounded-lg border border-border bg-card/50 p-3 space-y-2">
                                  <label className="text-xs font-semibold block">👍 Prova social (fake)</label>
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1"><span className="text-sm">👍</span><Input type="number" min={0} className="w-20" value={(b as any).fake_likes ?? 865} onChange={(e) => updateBlock(idx, { fake_likes: Number(e.target.value) })} /></div>
                                    <div className="flex items-center gap-1"><span className="text-sm">💬</span><Input type="number" min={0} className="w-20" value={(b as any).fake_comments ?? 216} onChange={(e) => updateBlock(idx, { fake_comments: Number(e.target.value) })} /></div>
                                    <div className="flex items-center gap-1"><span className="text-sm">↗</span><Input type="number" min={0} className="w-20" value={(b as any).fake_shares ?? 288} onChange={(e) => updateBlock(idx, { fake_shares: Number(e.target.value) })} /></div>
                                  </div>
                                </div>
                              </div>
                              )
                            })()}

                            {/* Rewarded template settings (gate de anúncio — tudo editável) */}
                            {b.template === 'rewarded' && (
                              <div className="space-y-3 rounded-lg border border-indigo-500/20 bg-indigo-500/5 p-3">
                                <span className="text-xs font-semibold text-indigo-400">🎁 Gate de Anúncio (Rewarded) — libera por TEMPO, não por clique</span>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Logo / marca</label>
                                    <Input value={(b as any).rw_logo ?? ''} onChange={(e) => updateBlock(idx, { rw_logo: e.target.value })} placeholder="Conteúdo+" />
                                  </div>
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Cor de destaque</label>
                                    <div className="flex gap-2 items-center">
                                      <input type="color" value={(b as any).accent_color ?? '#6366f1'} onChange={(e) => updateBlock(idx, { accent_color: e.target.value })} className="w-10 h-9 rounded border border-border bg-background p-0.5" />
                                      <Input value={(b as any).accent_color ?? '#6366f1'} onChange={(e) => updateBlock(idx, { accent_color: e.target.value })} className="flex-1 font-mono text-xs" />
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Título</label>
                                  <Input value={(b as any).rw_title ?? ''} onChange={(e) => updateBlock(idx, { rw_title: e.target.value })} placeholder="Conteúdo exclusivo liberado" />
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Subtítulo</label>
                                  <Input value={(b as any).rw_subtitle ?? ''} onChange={(e) => updateBlock(idx, { rw_subtitle: e.target.value })} placeholder="Veja um anúncio rápido…" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Botão "exibir anúncio"</label>
                                    <Input value={(b as any).rw_btn ?? ''} onChange={(e) => updateBlock(idx, { rw_btn: e.target.value })} placeholder="▶ EXIBIR ANÚNCIO" />
                                  </div>
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Aviso pequeno</label>
                                    <Input value={(b as any).rw_hint ?? ''} onChange={(e) => updateBlock(idx, { rw_hint: e.target.value })} placeholder="Você libera assim que o anúncio terminar." />
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Tempo do anúncio (s)</label>
                                    <Input type="number" min={3} max={30} value={(b as any).rw_timer ?? 5} onChange={(e) => updateBlock(idx, { rw_timer: Math.max(3, Math.min(30, Number(e.target.value))) })} />
                                  </div>
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Rótulo do ad</label>
                                    <Input value={(b as any).rw_ad_label ?? ''} onChange={(e) => updateBlock(idx, { rw_ad_label: e.target.value })} placeholder="Publicidade" />
                                  </div>
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Botão "continuar"</label>
                                    <Input value={(b as any).rw_continue ?? ''} onChange={(e) => updateBlock(idx, { rw_continue: e.target.value })} placeholder="CONTINUAR →" />
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Destino ao continuar (URL)</label>
                                  <Input value={(b as any).cta_url ?? ''} onChange={(e) => updateBlock(idx, { cta_url: e.target.value })} placeholder="https://destino.com/cadastro (vazio = recarrega)" className="font-mono text-xs" />
                                  <p className="text-[10px] text-muted-foreground/60 mt-1">Pra onde o lead vai ao tocar "Continuar". Ex: link do WhatsApp, formulário, site.</p>
                                </div>
                                <div className="rounded-md bg-amber-500/10 border border-amber-500/20 p-2">
                                  <p className="text-[11px] text-amber-500">⚠️ O anúncio (Adskeeper/GAM) vem da seção <strong>"Rotação de redes"</strong>. A liberação é por <strong>tempo</strong> — o clique no anúncio é opcional (CTR natural, anti-ban).</p>
                                </div>
                              </div>
                            )}

                            {/* Raw template settings */}
                            {b.template === 'raw' && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-lg border border-orange-500/20 bg-orange-500/5 p-3">
                                <div className="md:col-span-2">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={(b as any).show_loading !== false} onChange={(e) => updateBlock(idx, { show_loading: e.target.checked })} className="rounded" />
                                    <span className="text-xs font-medium">Loading (spinner + barra)</span>
                                  </label>
                                  <p className="text-[10px] text-muted-foreground/60 mt-1">Desligado = ad aparece direto, sem loading.</p>
                                </div>
                                <div className="md:col-span-2">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={(b as any).show_play_button === true} onChange={(e) => updateBlock(idx, { show_play_button: e.target.checked })} className="rounded" />
                                    <span className="text-xs font-medium">Botão Play</span>
                                  </label>
                                  <p className="text-[10px] text-muted-foreground/60 mt-1">Aparece após loading (ou direto se loading desligado). Cobre o ad.</p>
                                </div>
                                {(b as any).show_play_button === true && (
                                  <>
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Click passthrough (%)</label>
                                      <Input type="number" min={0} max={100} value={(b as any).click_passthrough ?? 50} onChange={(e) => updateBlock(idx, { click_passthrough: Number(e.target.value) })} />
                                      <p className="text-[10px] text-muted-foreground/60 mt-1">0% = sempre revela o ad. 100% = sempre clica no ad.</p>
                                    </div>
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Texto do botão Play</label>
                                      <Input value={(b as any).play_text ?? 'TOQUE PARA ASSISTIR'} onChange={(e) => updateBlock(idx, { play_text: e.target.value })} />
                                    </div>
                                  </>
                                )}
                                <div className="md:col-span-2">
                                  <label className="text-xs text-muted-foreground mb-1 block">Texto ACIMA do card</label>
                                  <Input value={(b as any).text_top ?? ''} onChange={(e) => updateBlock(idx, { text_top: e.target.value })} placeholder="👇 ASSISTA O VÍDEO LOGO ABAIXO 👇" />
                                  <p className="text-[10px] text-muted-foreground/60 mt-1">Indica o clique (público +45). Vazio = não mostra.</p>
                                </div>
                                <div className="md:col-span-2">
                                  <label className="text-xs text-muted-foreground mb-1 block">Texto ABAIXO do card</label>
                                  <Input value={(b as any).text_bottom ?? ''} onChange={(e) => updateBlock(idx, { text_bottom: e.target.value })} placeholder="☝️ TOQUE NA IMAGEM ACIMA PARA ASSISTIR ☝️" />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={(b as any).show_video_bar !== false} onChange={(e) => updateBlock(idx, { show_video_bar: e.target.checked })} className="rounded" />
                                    <span className="text-xs font-medium">Barra de player (vídeo pausado)</span>
                                  </label>
                                  <p className="text-[10px] text-muted-foreground/60 mt-1">Rodapé com ▶ 0:00 · progresso · duração. Parece um vídeo pausado.</p>
                                </div>
                                {(b as any).show_video_bar !== false && (
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Duração exibida</label>
                                    <Input value={(b as any).fake_duration ?? '3:42'} onChange={(e) => updateBlock(idx, { fake_duration: e.target.value })} placeholder="3:42" />
                                  </div>
                                )}
                                <div className="md:col-span-2">
                                  <label className="text-xs text-muted-foreground mb-1 block">Recarregar quando voltar do anúncio (segundos)</label>
                                  <Input type="number" min={0} max={120} className="w-24" value={(b as any).redirect_after_sec ?? 0} onChange={(e) => updateBlock(idx, { redirect_after_sec: Math.max(0, Math.min(120, Number(e.target.value))) })} />
                                  <p className="text-[10px] text-muted-foreground/60 mt-1">Quando a pessoa volta do anúncio, espera X segundos e recarrega a página — aí o overlay some (já apareceu nesta sessão) e ela fica no conteúdo. 0 = desligado.</p>
                                </div>
                              </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {!isWpp && (
                                <div className="md:col-span-2">
                                  <label className="text-xs text-muted-foreground mb-1 block">Mídias (aleatório a cada visita)</label>
                                  <div className="space-y-2">
                                    {((b as any).thumbnails ?? [(b as any).thumbnail].filter(Boolean)).map((url: string, ti: number) => (
                                      <div key={ti} className="flex gap-2 items-center">
                                        <div className="w-10 h-10 rounded overflow-hidden bg-muted flex-shrink-0">
                                          {/\.(mp4|webm|mov)/i.test(url) ? (
                                            <video src={url} className="w-full h-full object-cover" muted preload="metadata" />
                                          ) : url ? (
                                            <img src={url} className="w-full h-full object-cover" alt="" />
                                          ) : null}
                                        </div>
                                        <Input value={url} onChange={(e) => {
                                          const list = [...((b as any).thumbnails ?? [(b as any).thumbnail].filter(Boolean))];
                                          list[ti] = e.target.value;
                                          updateBlock(idx, { thumbnails: list, thumbnail: list[0] || '' });
                                        }} className="flex-1 text-xs" placeholder="URL da imagem ou vídeo" />
                                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" onClick={() => {
                                          const list = [...((b as any).thumbnails ?? [(b as any).thumbnail].filter(Boolean))].filter((_: string, i: number) => i !== ti);
                                          updateBlock(idx, { thumbnails: list, thumbnail: list[0] || '' });
                                        }}>
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      </div>
                                    ))}
                                    <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => {
                                      const list = [...((b as any).thumbnails ?? [(b as any).thumbnail].filter(Boolean)), ''];
                                      updateBlock(idx, { thumbnails: list });
                                      // Open picker for the new empty slot
                                    }}>
                                      <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar mídia
                                    </Button>
                                    <ImageUrlField value="" onChange={(url) => {
                                      const list = [...((b as any).thumbnails ?? [(b as any).thumbnail].filter(Boolean)), url];
                                      updateBlock(idx, { thumbnails: list, thumbnail: list[0] || '' });
                                    }} placeholder="Ou cole a URL aqui..." />
                                  </div>
                                </div>
                              )}
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Duração do loading (segundos)</label>
                                <Input type="number" min={2} max={30} value={b.duration ?? 8} onChange={(e) => updateBlock(idx, { duration: Number(e.target.value) })} />
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Cor destaque</label>
                                <div className="flex gap-2 items-center">
                                  <input type="color" value={b.accent_color ?? (isWpp ? '#25D366' : '#ff0050')} onChange={(e) => updateBlock(idx, { accent_color: e.target.value })} className="w-8 h-8 rounded border border-border cursor-pointer" />
                                  <Input value={b.accent_color ?? (isWpp ? '#25D366' : '#ff0050')} onChange={(e) => updateBlock(idx, { accent_color: e.target.value })} className="flex-1" />
                                </div>
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Texto do botão</label>
                                <Input value={b.play_text ?? (isWpp ? '🟢 INICIAR CONVERSA AGORA' : '▶ ASSISTIR AGORA')} onChange={(e) => updateBlock(idx, { play_text: e.target.value })} />
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Texto micro-commitment</label>
                                <Input value={b.micro_commit_text ?? 'Liberando acesso...'} onChange={(e) => updateBlock(idx, { micro_commit_text: e.target.value })} />
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Delay micro-commit (ms)</label>
                                <Input type="number" min={0} max={10000} step={500} value={b.micro_commit_delay ?? 2500} onChange={(e) => updateBlock(idx, { micro_commit_delay: Number(e.target.value) })} />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Texto do header (acima do player)</label>
                                <Input value={(b as any).header_text ?? ''} onChange={(e) => updateBlock(idx, { header_text: e.target.value })} placeholder="Ex: Conteúdo disponível 👇 (vazio = sem header)" />
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Texto do tap (após clique)</label>
                                <Input value={(b as any).tap_text ?? ''} onChange={(e) => updateBlock(idx, { tap_text: e.target.value })} placeholder="Ex: Toque para abrir ☝" />
                              </div>
                            </div>

                            {/* Live messages toggle */}
                            <div className="rounded-lg border border-border p-3 space-y-2">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={!!(b as any).live_messages} onChange={(e) => updateBlock(idx, { live_messages: e.target.checked })} className="rounded" />
                                <span className="text-xs font-medium">Mensagens "Online agora"</span>
                                <span className="text-[10px] text-muted-foreground">(avatar + mensagens provocativas rotativas)</span>
                              </label>
                              {!!(b as any).live_messages && (
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Mensagens (uma por linha, vazio = defaults)</label>
                                  <textarea
                                    value={((b as any).live_messages_list ?? []).join('\n')}
                                    onChange={(e) => updateBlock(idx, { live_messages_list: e.target.value.split('\n').filter(Boolean) })}
                                    className="w-full min-h-[80px] rounded-md border border-border bg-background px-3 py-2 text-xs font-mono resize-y"
                                    placeholder="Toque na imagem para ver meu video 😍👆&#10;Para abrir o video toque no botao 🔥👆&#10;Estou ao vivo agora, toque para assistir 😘"
                                  />
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Mensagens de status (uma por linha)</label>
                              <textarea
                                value={(b.status_messages ?? []).join('\n')}
                                onChange={(e) => updateBlock(idx, { status_messages: e.target.value.split('\n').filter(Boolean) })}
                                className="w-full min-h-[80px] rounded-md border border-border bg-background px-3 py-2 text-xs font-mono resize-y"
                                placeholder={'Preparando conexão segura...\nOtimizando buffer...\nPronto!'}
                              />
                            </div>

                            {isWpp && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Nome do contato</label>
                                  <Input value={b.wpp_contact_name ?? 'Consultoria Premium'} onChange={(e) => updateBlock(idx, { wpp_contact_name: e.target.value })} />
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Texto do header</label>
                                  <Input value={b.wpp_header_text ?? 'Conexão Segura'} onChange={(e) => updateBlock(idx, { wpp_header_text: e.target.value })} />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="text-xs text-muted-foreground mb-1 block">Foto de perfil</label>
                                  <div className="flex items-center gap-3">
                                    {b.wpp_avatar_url ? (
                                      <img src={b.wpp_avatar_url} alt="" className="w-10 h-10 rounded-full object-cover border border-border flex-shrink-0" />
                                    ) : (
                                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-content-center flex-shrink-0 border border-border" />
                                    )}
                                    <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setAvatarPickerIdx(idx)}>
                                      <ImageIcon className="h-3.5 w-3.5" />
                                      {b.wpp_avatar_url ? 'Trocar' : 'Escolher da biblioteca'}
                                    </Button>
                                    {b.wpp_avatar_url && (
                                      <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={() => updateBlock(idx, { wpp_avatar_url: '' })}>
                                        Remover
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Security Gate fields */}
                            {isSG && (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Label do topo</label>
                                    <Input value={(b as any).gate_label ?? 'SECURITY CHECK'} onChange={(e) => updateBlock(idx, { gate_label: e.target.value })} />
                                  </div>
                                  <div>
                                    <label className="text-xs text-muted-foreground mb-1 block">Estilo do botão</label>
                                    <select value={(b as any).button_style ?? 'filled'} onChange={(e) => updateBlock(idx, { button_style: e.target.value })} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                                      <option value="filled">Filled (sólido)</option>
                                      <option value="outline">Outline (borda)</option>
                                    </select>
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Mensagens do Gate (uma por linha)</label>
                                  <textarea
                                    value={((b as any).gate_messages ?? []).join('\n')}
                                    onChange={(e) => updateBlock(idx, { gate_messages: e.target.value.split('\n').filter((l: string) => l.trim()) })}
                                    className="w-full min-h-[60px] rounded-md border border-border bg-background px-3 py-2 text-xs font-mono resize-y"
                                    placeholder="Analisando segurança..."
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Hint abaixo do botão</label>
                                  <Input value={(b as any).gate_hint ?? ''} onChange={(e) => updateBlock(idx, { gate_hint: e.target.value })} />
                                </div>
                                <div className="flex items-center gap-2 py-1">
                                  <input type="checkbox" checked={(b as any).external_verify ?? true} onChange={(e) => updateBlock(idx, { external_verify: e.target.checked })} className="rounded" />
                                  <label className="text-xs text-muted-foreground">External Verify (abre ad em nova aba + focus listener)</label>
                                </div>
                                {(b as any).external_verify !== false && (
                                  <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-blue-500/20">
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Tempo mínimo (seg)</label>
                                      <Input type="number" value={Array.isArray((b as any).verify_time) ? (b as any).verify_time[0] : ((b as any).verify_time ?? 7)} onChange={(e) => {
                                        const v = parseInt(e.target.value) || 7;
                                        const cur = (b as any).verify_time;
                                        const max = Array.isArray(cur) ? cur[1] : v + 2;
                                        updateBlock(idx, { verify_time: [v, Math.max(v, max)] });
                                      }} min={2} max={30} />
                                    </div>
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Tempo máximo (seg)</label>
                                      <Input type="number" value={Array.isArray((b as any).verify_time) ? (b as any).verify_time[1] : (((b as any).verify_time ?? 7) + 2)} onChange={(e) => {
                                        const v = parseInt(e.target.value) || 9;
                                        const cur = (b as any).verify_time;
                                        const min = Array.isArray(cur) ? cur[0] : v - 2;
                                        updateBlock(idx, { verify_time: [Math.min(min, v), v] });
                                      }} min={2} max={30} />
                                    </div>
                                    <div className="col-span-2">
                                      <label className="text-xs text-muted-foreground mb-1 block">Mensagem de espera</label>
                                      <Input value={(b as any).verify_message ?? ''} onChange={(e) => updateBlock(idx, { verify_message: e.target.value })} />
                                    </div>
                                    <div className="col-span-2">
                                      <label className="text-xs text-muted-foreground mb-1 block">Mensagem retorno precoce</label>
                                      <Input value={(b as any).verify_early_message ?? ''} onChange={(e) => updateBlock(idx, { verify_early_message: e.target.value })} />
                                    </div>
                                    <div className="col-span-2">
                                      <label className="text-xs text-muted-foreground mb-1 block">Fallback URL (se ad não carregar)</label>
                                      <Input value={(b as any).fallback_url ?? ''} onChange={(e) => updateBlock(idx, { fallback_url: e.target.value })} placeholder="https://... (vazio = libera após timer)" />
                                    </div>
                                  </div>
                                )}
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Mensagens Double-Dip (uma por linha)</label>
                                  <textarea
                                    value={((b as any).dd_messages ?? []).join('\n')}
                                    onChange={(e) => updateBlock(idx, { dd_messages: e.target.value.split('\n').filter((l: string) => l.trim()) })}
                                    className="w-full min-h-[40px] rounded-md border border-border bg-background px-3 py-2 text-xs font-mono resize-y"
                                    placeholder="Preparando conteúdo..."
                                  />
                                </div>

                                {/* Gallery preview images (blurred OnlyFans/Privacy style) */}
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Galeria Preview (imagens borradas estilo Privacy/OnlyFans)</label>
                                  <p className="text-[10px] text-muted-foreground/60 mb-2">Imagens aparecem borradas com 🔒 no overlay. Cria urgência para desbloquear.</p>
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {((b as any).gate_images ?? []).map((url: string, imgIdx: number) => (
                                      <div key={imgIdx} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-border">
                                        <img src={url} alt="" className="w-full h-full object-cover blur-[2px]" />
                                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center text-white text-xs">🔒</div>
                                        <button
                                          onClick={() => {
                                            const imgs = [...((b as any).gate_images ?? [])];
                                            imgs.splice(imgIdx, 1);
                                            updateBlock(idx, { gate_images: imgs });
                                          }}
                                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive text-white text-[9px] leading-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        >×</button>
                                      </div>
                                    ))}
                                    <button
                                      onClick={() => setGateImgPickerIdx(idx)}
                                      className="w-16 h-16 rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center gap-0.5 text-muted-foreground hover:text-primary transition-colors"
                                    >
                                      <Plus className="h-4 w-4" />
                                      <span className="text-[9px]">Adicionar</span>
                                    </button>
                                  </div>
                                  {((b as any).gate_images ?? []).length > 0 && (
                                    <p className="text-[10px] text-muted-foreground">{((b as any).gate_images ?? []).length} imagem(ns) — aparecerão em grid 2x2 borrado no overlay</p>
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Provedor de Ads</label>
                                <select
                                  value={(b as any).ad_provider ?? 'auto'}
                                  onChange={(e) => updateBlock(idx, { ad_provider: e.target.value })}
                                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                >
                                  <option value="auto">Auto-detectar (AdSense → Groone → GPT)</option>
                                  <option value="adsense">AdSense apenas</option>
                                  <option value="groone">Groone / AdX apenas</option>
                                  <option value="adskeeper">Adskeeper (MGID)</option>
                                </select>
                              </div>
                              {(b as any).ad_provider !== 'adskeeper' && (
                              <>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Ad Unit (GAM)</label>
                                <Input
                                  value={(b as any).ad_unit ?? ''}
                                  onChange={(e) => updateBlock(idx, { ad_unit: e.target.value })}
                                  placeholder="/23353895524/1"
                                  className="font-mono text-xs"
                                />
                                <p className="text-[10px] text-muted-foreground mt-1">Ex: /network_code/slot. Preencha pra usar GPT direto.</p>
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Quantidade de Ads</label>
                                <select
                                  value={(b as any).ad_count ?? '2'}
                                  onChange={(e) => updateBlock(idx, { ad_count: e.target.value })}
                                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                >
                                  <option value="2">2 ads (durante loading + após clique)</option>
                                  <option value="1">1 ad (só após clique)</option>
                                </select>
                              </div>
                              </>
                              )}
                            </div>
                            {(b as any).ad_provider === 'adskeeper' && (
                            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 space-y-2">
                              <p className="text-[11px] text-blue-600 dark:text-blue-400">🔵 <strong>Adskeeper direto</strong> — renderiza inline no documento real (URL verdadeira, o MGID entrega). Não depende da Rotação de redes. Loader/Widget abaixo são a <strong>mesma config</strong> da aba Rotação de redes (edita de qualquer lugar).</p>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Loader (vai no &lt;head&gt;, 1x)</label>
                                <textarea
                                  rows={2}
                                  value={dp.adskeeper?.loader ?? ''}
                                  onChange={(e) => patchDp({ adskeeper: { ...(dp.adskeeper ?? {}), loader: e.target.value } })}
                                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono"
                                  placeholder='<script src="https://jsc.adskeeper.com/site/XXXXX.js" async></script>'
                                />
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Widget (vai no &lt;body&gt;)</label>
                                <textarea
                                  rows={3}
                                  value={dp.adskeeper?.widget ?? ''}
                                  onChange={(e) => patchDp({ adskeeper: { ...(dp.adskeeper ?? {}), widget: e.target.value } })}
                                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono"
                                  placeholder='<div data-type="_mgwidget" data-widget-id="XXXXX"></div><script>...</script>'
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-muted-foreground">Clique forçado (passthrough)</label>
                                <Input type="number" min={0} max={100} className="w-20"
                                  value={dp.adskeeper?.passthrough ?? 60}
                                  onChange={(e) => patchDp({ adskeeper: { ...(dp.adskeeper ?? {}), passthrough: Math.max(0, Math.min(100, Number(e.target.value))) } })} />
                                <span className="text-xs text-muted-foreground">%</span>
                              </div>
                            </div>
                            )}
                            {(b as any).ad_provider !== 'adskeeper' && (b as any).ad_count !== '1' && (
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Ad 1 — durante o loading (dentro do overlay)</label>
                              <textarea
                                value={b.code}
                                onChange={(e) => updateBlock(idx, { code: e.target.value })}
                                className="w-full min-h-[60px] rounded-md border border-border bg-background px-3 py-2 text-xs font-mono resize-y"
                                placeholder="HTML do anúncio (vazio = auto-detectar)"
                              />
                            </div>
                            )}
                            {(b as any).ad_provider !== 'adskeeper' && (
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Ad 2 — após o unlock (atrás do overlay)</label>
                              <textarea
                                value={(b as any).code_2 ?? ''}
                                onChange={(e) => updateBlock(idx, { code_2: e.target.value })}
                                className="w-full min-h-[60px] rounded-md border border-border bg-background px-3 py-2 text-xs font-mono resize-y"
                                placeholder="HTML do 2º anúncio (vazio = usa o Ad 1)"
                              />
                            </div>
                            )}
                          </div>
                        );
                      })()}

                      {block.type === 'arrows' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Quantidade de setas</label>
                            <Input type="number" min={1} max={5} value={(block as ArrowsBlock).count} onChange={(e) => updateBlock(idx, { count: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Cor</label>
                            <div className="flex gap-2 items-center">
                              <input type="color" value={(block as ArrowsBlock).color} onChange={(e) => updateBlock(idx, { color: e.target.value })} className="w-8 h-8 rounded border border-border cursor-pointer" />
                              <Input value={(block as ArrowsBlock).color} onChange={(e) => updateBlock(idx, { color: e.target.value })} className="flex-1" />
                            </div>
                          </div>
                        </div>
                      )}

                      {block.type === 'loading' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="md:col-span-2">
                            <label className="text-xs text-muted-foreground mb-1 block">Texto</label>
                            <Input value={(block as LoadingBlock).text} onChange={(e) => updateBlock(idx, { text: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Cor</label>
                            <div className="flex gap-2 items-center">
                              <input type="color" value={(block as LoadingBlock).color} onChange={(e) => updateBlock(idx, { color: e.target.value })} className="w-8 h-8 rounded border border-border cursor-pointer" />
                              <Input value={(block as LoadingBlock).color} onChange={(e) => updateBlock(idx, { color: e.target.value })} className="flex-1" />
                            </div>
                          </div>
                        </div>
                      )}

                      {block.type === 'cta' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Texto do botão</label>
                            <Input value={(block as CtaBlock).text} onChange={(e) => updateBlock(idx, { text: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">URL de destino</label>
                            <Input value={(block as CtaBlock).url} onChange={(e) => updateBlock(idx, { url: e.target.value })} placeholder="https://..." />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Cor</label>
                            <div className="flex gap-2 items-center">
                              <input type="color" value={(block as CtaBlock).color} onChange={(e) => updateBlock(idx, { color: e.target.value })} className="w-8 h-8 rounded border border-border cursor-pointer" />
                              <Input value={(block as CtaBlock).color} onChange={(e) => updateBlock(idx, { color: e.target.value })} className="flex-1" />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Dica (abaixo do botão)</label>
                            <Input value={(block as CtaBlock).hint} onChange={(e) => updateBlock(idx, { hint: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Delay (s, 0 = imediato)</label>
                            <Input type="number" min={0} value={(block as CtaBlock).delay} onChange={(e) => updateBlock(idx, { delay: Number(e.target.value) })} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Live Phone Preview */}
          <div className="hidden lg:block">
            <div className="sticky top-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold">Preview</h3>
                <Button size="sm" variant="ghost" onClick={openPreview} disabled={previewLoading} className="h-7 text-xs gap-1">
                  {previewLoading ? <><div className="h-3 w-3 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" /> Carregando...</> : <><Eye className="h-3 w-3" /> Atualizar</>}
                </Button>
              </div>
              <div
                className="rounded-[1.5rem] border-[3px] border-zinc-700 bg-black overflow-hidden mx-auto relative"
                style={{ width: 280, height: 500 }}
              >
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90px] h-[18px] bg-black rounded-b-xl z-10" />
                {previewHtml ? (
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full h-full border-none"
                    sandbox="allow-scripts allow-same-origin"
                    title="Overlay Preview"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-muted-foreground" style={{ background: cfg.bg_color }}>
                    <Smartphone className="h-8 w-8 opacity-30" />
                    <p className="text-xs opacity-50">Clique "Atualizar" para ver o preview</p>
                  </div>
                )}
              </div>
              {cfg.permanent && (
                <p className="text-xs text-center text-muted-foreground mt-2">Modo permanente — sem fechar</p>
              )}
            </div>
          </div>

          </div>{/* close grid */}

          {/* Info */}
          <div className="rounded-xl border border-dashed border-border bg-muted/30 p-4 text-xs text-muted-foreground space-y-1">
            <p><strong>Como funciona:</strong></p>
            <p>• Só aparece em celulares (tela &lt; 768px)</p>
            <p>• Só aparece para quem vem do Instagram ou Facebook (referrer ou fbclid/utm)</p>
            <p>• Aparece 1x por sessão (não repete se o usuário navegar)</p>
            <p>• O WordPress e AdSense carregam normalmente por baixo</p>
            <p>• Blocos habilitados são renderizados na ordem da lista</p>
          </div>
        </>
      )}

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={save} disabled={updateSite.isPending}>
          <Save className="h-4 w-4 mr-1" />
          {updateSite.isPending ? 'Salvando...' : 'Salvar Overlay'}
        </Button>
      </div>

      {/* Avatar picker from media library */}
      <MediaPickerModal
        open={avatarPickerIdx !== null}
        onClose={() => setAvatarPickerIdx(null)}
        onSelect={(url) => {
          if (avatarPickerIdx !== null) updateBlock(avatarPickerIdx, { wpp_avatar_url: url })
          setAvatarPickerIdx(null)
        }}
      />

      {/* Gate gallery images picker (multi-select — appends, stays open) */}
      <MediaPickerModal
        open={gateImgPickerIdx !== null}
        onClose={() => setGateImgPickerIdx(null)}
        keepOpen
        onSelect={(url) => {
          if (gateImgPickerIdx !== null) {
            const cur = ((cfg.blocks[gateImgPickerIdx] as any)?.gate_images ?? []) as string[]
            updateBlock(gateImgPickerIdx, { gate_images: [...cur, url] })
          }
        }}
      />
    </div>
  )
}

function RealtimeView({ site }: { site: TrackerSite }) {
  const { t } = useLanguage()
  const { data: rt } = useTrackerRealtime(site.id)
  const { data: snippet } = useTrackerSnippet(site.id)
  const [tab, setTab] = useState<'realtime' | 'history' | 'quality' | 'analytics' | 'protection' | 'overlay' | 'snippet'>('realtime')
  const { data: qualityRt } = useTrackerQualityRealtime(site.id)
  const { data: history } = useTrackerHistory(site.id, 30)

  // Seletor de período da aba Tempo Real (presets). 'today' = Redis ao vivo; resto = MySQL.
  const [period, setPeriod] = useState<'today' | 'yesterday' | '7d' | '30d'>('today')
  const isToday = period === 'today'
  const { data: periodHist } = useTrackerHistoryPeriod(site.id, period, !isToday)
  const PERIODS = [
    { id: 'today' as const, label: 'Hoje' },
    { id: 'yesterday' as const, label: 'Ontem' },
    { id: '7d' as const, label: '7 dias' },
    { id: '30d' as const, label: '30 dias' },
  ]
  // Fonte unificada de métricas conforme o período (normaliza Redis vs MySQL).
  const periodViews = isToday ? (rt?.views_today ?? 0) : (periodHist?.daily ?? []).reduce((a, d) => a + Number(d.views), 0)
  const periodUnique = isToday ? (rt?.unique_today ?? 0) : (periodHist?.daily ?? []).reduce((a, d) => a + Number(d.unique_visitors), 0)
  const periodChart = isToday
    ? { data: rt?.hourly ?? [], title: 'Últimas 24h', labelKey: 'hour', valueKey: 'views', secondaryKey: undefined as string | undefined }
    : { data: (periodHist?.daily ?? []).map((d) => ({ ...d, label: d.date.slice(5) })), title: PERIODS.find((p) => p.id === period)?.label ?? '', labelKey: 'label', valueKey: 'views', secondaryKey: 'unique_visitors' as string | undefined }
  const pagesRows: [string, number][] = isToday
    ? Object.entries(rt?.pages ?? {}).slice(0, 10) as [string, number][]
    : (periodHist?.pages ?? []).slice(0, 10).map((p) => [p.page_path, Number(p.views)])
  const refRows: [string, number][] = isToday
    ? Object.entries(rt?.referrers ?? {}).slice(0, 10) as [string, number][]
    : (periodHist?.referrers ?? []).slice(0, 10).map((r) => [r.referrer_domain || '(direto)', Number(r.views)])
  const devRows: [string, number][] = isToday
    ? (rt?.devices ?? []).slice(0, 10).map((d) => [`${d.type} / ${d.browser}`, d.views])
    : (periodHist?.devices ?? []).slice(0, 10).map((d) => [`${d.device_type} / ${d.browser}`, Number(d.views)])
  const locRows: [string, number][] = isToday
    ? (rt?.locations ?? []).slice(0, 10).map((l) => [`${l.city || l.region || l.country}${l.country ? ` (${l.country})` : ''}`, l.views])
    : (periodHist?.locations ?? []).slice(0, 10).map((l) => [`${l.city || l.region || l.country}${l.country ? ` (${l.country})` : ''}`, Number(l.views)])
  const campRows = isToday ? (rt?.campaigns ?? []) : (periodHist?.campaigns ?? []).map((c) => ({ source: c.utm_source, medium: c.utm_medium, campaign: c.utm_campaign, views: Number(c.views) }))

  const copySnippet = () => {
    if (snippet?.snippet) {
      navigator.clipboard.writeText(snippet.snippet)
      toast.success('Snippet copiado!')
    }
  }

  const tabs = [
    { id: 'realtime' as const, label: 'Tempo Real', icon: Activity },
    { id: 'history' as const, label: t('Historico'), icon: BarChart3 },
    { id: 'quality' as const, label: 'Qualidade', icon: Zap },
    { id: 'analytics' as const, label: 'Analytics', icon: BarChart3 },
    { id: 'protection' as const, label: 'Proteção', icon: Shield },
    { id: 'overlay' as const, label: 'Overlay', icon: Layers },
    { id: 'snippet' as const, label: 'Snippet', icon: Link2 },
  ]

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-lg bg-muted/50 w-fit">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === tb.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <tb.icon className="h-3.5 w-3.5" />
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'realtime' && rt && (
        <div className="space-y-4">
          {/* Seletor de período + online ao vivo */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-1 p-1 rounded-lg bg-muted/50 w-fit">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPeriod(p.id)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    period === p.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {isToday && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={`inline-block w-2 h-2 rounded-full ${rt.online > 0 ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground/40'}`} />
                {rt.online} online agora
              </span>
            )}
          </div>

          {/* KPIs */}
          <div className={`grid gap-3 ${isToday ? (qualityRt ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3') : 'grid-cols-2'}`}>
            {isToday && (
              <KpiCard label="Online Agora" value={rt.online} icon={Users} color="bg-emerald-500/15 text-emerald-500" pulse={rt.online > 0} />
            )}
            <KpiCard label={isToday ? 'Views Hoje' : 'Views no período'} value={periodViews} icon={Eye} color="bg-blue-500/15 text-blue-500" />
            <KpiCard label={isToday ? t('Unicos Hoje') : 'Únicos no período'} value={periodUnique} icon={Users} color="bg-purple-500/15 text-purple-500" />
            {isToday && qualityRt && (
              <KpiCard
                label="Score Médio"
                value={String(qualityRt.avg_score)}
                icon={Zap}
                color={qualityRt.avg_score >= 70 ? 'bg-red-500/15 text-red-500' : qualityRt.avg_score >= 40 ? 'bg-yellow-500/15 text-yellow-500' : 'bg-blue-500/15 text-blue-500'}
              />
            )}
          </div>

          {/* Chart: por hora (hoje) ou por dia (períodos) */}
          <TrackerBarChart
            data={periodChart.data as any}
            title={periodChart.title}
            titleIcon={isToday ? Clock : BarChart3}
            labelKey={periodChart.labelKey}
            valueKey={periodChart.valueKey}
            secondaryKey={periodChart.secondaryKey}
          />

          {/* Data tables in grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DataTable title={t('Paginas')} icon={Globe} data={pagesRows} columns={['Path', 'Views']} />
            <DataTable title="Referrers" icon={ArrowUpRight} data={refRows} columns={[t('Dominio'), 'Views']} />
            <DataTable title={t('Dispositivos')} icon={MonitorSmartphone} data={devRows} columns={[t('Dispositivo'), 'Views']} />
            <DataTable title={t('Localizacao')} icon={MapPin} data={locRows} columns={[t('Local'), 'Views']} />
          </div>

          {/* UTM Campaigns */}
          {campRows && campRows.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-muted-foreground" />
                Campanhas (UTM)
              </h3>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Source</th>
                      <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Medium</th>
                      <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">{t('Campanha')}</th>
                      <th className="text-right px-3 py-1.5 text-xs font-medium text-muted-foreground">Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campRows.map((c, i) => (
                      <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/20">
                        <td className="px-3 py-1.5">{c.source || '-'}</td>
                        <td className="px-3 py-1.5">{c.medium || '-'}</td>
                        <td className="px-3 py-1.5">{c.campaign || '-'}</td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums">{c.views}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && history && (
        <div className="space-y-4">
          {/* Daily chart */}
          {history.daily.length > 0 ? (
            <TrackerBarChart
              data={history.daily.map((d) => ({ ...d, label: d.date.slice(5) }))}
              title="Ultimos 30 dias"
              titleIcon={BarChart3}
              labelKey="label"
              valueKey="views"
              secondaryKey="unique_visitors"
            />
          ) : (
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="text-sm font-semibold mb-3">Ultimos 30 dias</h3>
              <p className="text-sm text-muted-foreground">Sem dados historicos ainda. O sistema salva dados a cada 5 minutos.</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DataTable
              title={t('Paginas')}
              icon={Globe}
              data={history.pages.slice(0, 15).map((p) => [p.page_path, Number(p.views)])}
              columns={['Path', 'Views']}
            />
            <DataTable
              title="Referrers"
              icon={ArrowUpRight}
              data={history.referrers.slice(0, 15).map((r) => [r.referrer_domain, Number(r.views)])}
              columns={[t('Dominio'), 'Views']}
            />
          </div>
        </div>
      )}

      {tab === 'quality' && <QualityTab siteId={site.id} />}

      {tab === 'analytics' && <AnalyticsTab siteId={site.id} />}
      {tab === 'protection' && <ProtectionTab siteId={site.id} />}

      {tab === 'overlay' && <OverlayEditorV2 site={site} />}

      {tab === 'snippet' && (
        <div className="space-y-4">
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div>
            <h3 className="text-sm font-semibold mb-1">Snippet de Rastreamento</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Cole este codigo no header do seu WordPress (ou qualquer site HTML).
            </p>
          </div>
          <div className="relative">
            <pre className="bg-muted/50 rounded-lg p-4 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all border border-border">
              {snippet?.snippet || 'Carregando...'}
            </pre>
            <Button size="sm" variant="outline" className="absolute top-2 right-2" onClick={copySnippet}>
              <Copy className="h-3 w-3 mr-1" />
              {t('Copiar')}
            </Button>
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <p><strong>WordPress:</strong> Aparencia &rarr; Editor de Tema &rarr; header.php &rarr; cole antes de &lt;/head&gt;</p>
            <p><strong>Ou:</strong> Use o plugin "Insert Headers and Footers" e cole no campo Header.</p>
          </div>
        </div>
        <ProtectedLinkGenerator site={site} />
        </div>
      )}
    </div>
  )
}

export function Tracker() {
  const { t } = useLanguage()
  const { data: sites, isLoading } = useTrackerSites()
  const createSite = useCreateTrackerSite()
  const deleteSite = useDeleteTrackerSite()

  const [selectedSiteId, setSelectedSiteId] = useState<string>('')
  const [showNew, setShowNew] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDomain, setNewDomain] = useState('')

  const selectedSite = sites?.find((s) => s.id === selectedSiteId)

  // Auto-select first site
  if (sites?.length && !selectedSiteId && !selectedSite) {
    setTimeout(() => setSelectedSiteId(sites[0].id), 0)
  }

  const handleCreate = () => {
    if (!newName.trim()) return
    createSite.mutate(
      { name: newName.trim(), domain: newDomain.trim() || undefined },
      {
        onSuccess: (site) => {
          toast.success('Site criado!')
          setNewName('')
          setNewDomain('')
          setShowNew(false)
          setTimeout(() => setSelectedSiteId(site.id), 100)
        },
        onError: () => toast.error('Erro ao criar site'),
      }
    )
  }

  const handleDelete = (id: string) => {
    if (!confirm('Excluir este site e todos os dados?')) return
    deleteSite.mutate(id, {
      onSuccess: () => {
        toast.success('Site excluido')
        if (selectedSiteId === id) setSelectedSiteId(sites?.find((s) => s.id !== id)?.id || '')
      },
    })
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">{t('Rastreamento')}</h1>
          <p className="text-sm text-muted-foreground">{t('Rastreie visitantes dos seus sites em tempo real')}</p>
        </div>
        <Button size="sm" onClick={() => setShowNew(!showNew)}>
          <Plus className="h-4 w-4 mr-1" />
          Novo Site
        </Button>
      </div>

      {/* New site form */}
      {showNew && (
        <div className="rounded-xl border border-border bg-card p-4 flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="Nome do site (ex: Blog Principal)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="flex-1"
          />
          <Input
            placeholder={t('Dominio (ex: meusite.com.br)')}
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            className="flex-1"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreate} disabled={createSite.isPending}>
              {createSite.isPending ? 'Criando...' : 'Criar'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowNew(false)}>
              {t('Cancelar')}
            </Button>
          </div>
        </div>
      )}

      {/* Site selector */}
      {sites && sites.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sites.map((site) => (
            <div
              key={site.id}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                selectedSiteId === site.id
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setSelectedSiteId(site.id)}
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm font-medium">{site.name}</span>
              {site.domain && <span className="text-xs text-muted-foreground">({site.domain})</span>}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDelete(site.id)
                }}
                className="ml-1 p-0.5 rounded hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dashboard */}
      {selectedSite ? (
        <RealtimeView site={selectedSite} />
      ) : isLoading ? (
        <div className="text-center py-20 text-muted-foreground">{t('Carregando...')}</div>
      ) : (
        <div className="text-center py-20">
          <Globe className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
          <h3 className="text-lg font-semibold mb-1">Nenhum site cadastrado</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Crie um site para comecar a rastrear visitantes em tempo real.
          </p>
          <Button onClick={() => setShowNew(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Novo Site
          </Button>
        </div>
      )}
    </div>
  )
}
